import { useToast } from '@/composables/useToast.js';
import { useTillStore } from '@/stores/till-store.js';
import apiClient from '@/utils/axios.js';
import { newClientRequestId } from '@/utils/clientRequestId.js';
import { defineStore } from 'pinia';
import { toRaw } from 'vue';

import offlineDB, {
    cacheVariantAvailability,
    captureOfflineCacheWriteEpoch,
    clearStoreState,
    finalizeSaleAllocations,
    loadStoreState,
    getPosCatalogRowsByIds,
    resetAllReservations,
    saveStoreState,
    syncBranchStock,
} from '@/indexedDB/offlineDB.js';
import { canMutateOnline } from '@/services/connectivityState.js';
import {
    getPosSessionId,
    isPosSessionAdoptable,
    livePosSessionIds,
    POS_SESSION_ADOPT_MS,
    publishPosReservationChange,
    subscribePosReservationChanges,
} from '@/services/offlineIdentity.js';
import {
    appliedToDocumentCents,
    calculateSaleSummary,
    crossCents,
    documentToTenderCents,
    exclusiveRateFrom,
    fromScaled,
    lineDiscountUnits,
    lineGrossUnits,
    normalizeNonNegativeNumber,
    percentOf,
    roundMoney,
    roundRate,
    roundTo,
    scaleFor,
    splitTender,
    toBasisPoints,
    toScaled,
} from '@/services/saleCalculations.js';
import { useConfigurationStore } from '@/stores/configuration-store.js';
import { todayLocal } from '@/utils/businessDate.js';

let persistChain = Promise.resolve();
let persistTimer = null;
let persistGeneration = 0;
let customerRequestSequence = 0;
const latestCustomerRequestByTab = new Map();
let reservationGeneration = 0;
let reservationChain = Promise.resolve();

/**
 * The rate a cart line carries.
 *
 * The catalogue prices goods tax-inclusive — `branch_variants.sales_price` is
 * the shelf price — but the sale chain is a §2(46) one: the rate, both discounts
 * and the value of supply all exclude sales tax, and the tax is added back at
 * the end. Converting here rather than in the calculator means the figure the
 * cashier sees, the base a percentage discount is taken from, the cap on a rupee
 * discount and the `amount` submitted to CreateSaleJob are all the same number.
 *
 * A line whose goods carry no tax converts to itself.
 */
const cartRateFrom = (product, taxPercentage) => exclusiveRateFrom(product?.rate ?? 0, taxPercentage, saleDecimals());

/**
 * The details a cart line may show under its name (family, SKU, material,
 * colour, size, type, dimensions), copied from the search payload and then
 * the branch cache in the same order unit_abbr is. Display-only: the till
 * prints them by the pos_line_* switches and the server reads none of them
 * from the checkout.
 */
const lineDetailFieldsFrom = (product, record = null) => ({
    family_name: product?.family_name ?? record?.family_name ?? '',
    sku: product?.sku ?? record?.sku ?? '',
    material: product?.material ?? record?.material ?? '',
    color: product?.color ?? record?.color ?? '',
    size: product?.size ?? record?.size ?? '',
    type: product?.type ?? record?.type ?? '',
    length: product?.length ?? record?.length ?? null,
    width: product?.width ?? record?.width ?? null,
    height: product?.height ?? record?.height ?? null,
});

/**
 * The places this installation computes a sale at — the `sale_decimal_places`
 * setting, which the server checks the submitted cart against.
 */
function saleDecimals() {
    return useConfigurationStore().saleDecimalPlaces;
}

/** Whether the cart settles its payable to the whole rupee: the setting, which the server holds the submitted cart to. */
function saleRoundTotal() {
    return useConfigurationStore().saleRoundTotal;
}

/**
 * Multi-currency (docs/multi-currency-plan.md). A tab's sale may be in a
 * currency other than the base: every money figure on its lines is then in that
 * currency. The catalogue is priced in base, so a line added to such a tab is
 * converted on the way in, and switching a tab's currency converts every line
 * from the old to the new — through base, at the two rates, one rounding per
 * figure at the sale's places. Costs never live on a cart line, and a worker's
 * figures (own rate, typed rate or base) are what the worker earns — base,
 * whatever the customer is charged in — so they are not converted either.
 */
const LINE_MONEY_FIELDS = ['rate', 'list_rate', 'mrp', 'damage_price', 'shelf_price'];
const RS_DISCOUNT_FIELDS = [
    ['discount', 'discount_type'],
    ['base_discount', 'base_discount_type'],
];

/** One figure from one currency to another (`fromRate`/`toRate` are base per unit; '1' is the base). */
const convertFigure = (value, fromRate, toRate) => {
    if (value === null || value === undefined || value === '') return value;
    const number = Number(value);
    if (!Number.isFinite(number) || number === 0) return value;
    const scale = scaleFor(saleDecimals());
    const units = toScaled(Math.abs(number), scale);
    const converted = fromScaled(crossCents(units, fromRate, toRate), scale);
    return number < 0 ? -converted : converted;
};

const convertLineMoney = (item, fromRate, toRate) => {
    if (!item || String(fromRate) === String(toRate)) return item;
    const next = { ...item };
    for (const field of LINE_MONEY_FIELDS) {
        if (field in next) next[field] = convertFigure(next[field], fromRate, toRate);
    }
    // A rupee discount is money; a percentage is not.
    for (const [field, typeField] of RS_DISCOUNT_FIELDS) {
        if (field in next && next[typeField] !== 'percent') next[field] = convertFigure(next[field], fromRate, toRate);
    }
    return next;
};

/** The rate a tab's lines are priced at: '1' for a base sale. */
const tabRate = (tab) => (tab?.form?.currency_code ? String(tab.form.exchange_rate || '1') : '1');

/** A line built from the base catalogue, in the tab's currency. */
const inTabCurrency = (tab, item) => (tab?.form?.currency_code ? convertLineMoney(item, '1', tabRate(tab)) : item);

/** A tab's form with the precision the cart is priced at, and whether its total rounds to the rupee, for the calculator. */
const pricedForm = (form) => ({ ...(form ?? {}), decimals: saleDecimals(), round_total: saleRoundTotal() });

/**
 * Batch mode: the batch a search row stood for, carried onto its cart line.
 *
 * Empty for a row that is not a batch (batch mode off, a service), so such a
 * line is exactly the shape it always was. A line is sold from its batch alone
 * — the server refuses one its batch cannot cover rather than take the rest
 * from another — so `batch_qty`, what the row said was left, caps the line the
 * way `damage_qty` caps a damaged one, and a quantity past it continues onto
 * the next batch's own line (continueOntoNextBatches). The "no batch" row
 * carries the same cap for the unbatched stock, with no `receiving_item_id`.
 */
const batchFieldsFrom = (product) => ({
    ...(product?.receiving_item_id
        ? {
              receiving_item_id: Number(product.receiving_item_id),
              batch_number: product.batch_number ?? '',
              batch_expiry: product.batch_expiry ?? null,
          }
        : {}),
    ...(product?.batch_qty != null && Number.isFinite(Number(product.batch_qty))
        ? { batch_qty: Math.max(0, normalizeQuantity(product.batch_qty)) }
        : {}),
});

/**
 * Batch mode: a POS search row as the rows the till lists — one per sellable
 * batch, earliest expiry first, each at its own price, then the stock outside
 * every batch, then the expired batches still holding stock, listed so the
 * cashier can see that stock and why it is not on offer but never pickable.
 * `rate` is the tax-inclusive shelf price the cart converts from, so the
 * batch's figure goes there; `tax_rate` is the tax inside it and has to move
 * with it. A batch received without a price keeps the product's. A row that is
 * not batched (a service, a damaged report, batch mode off) is itself.
 *
 * The one place a batch becomes a row: the search lists these, and the cart's
 * batch continuation adds the next of them.
 */
export const posBatchRows = (product) => {
    const sellable = Array.isArray(product?.batches) ? product.batches : [];
    const expired = Array.isArray(product?.expired_batches) ? product.expired_batches : [];
    if (product.is_service || product.damaged_item_id != null || sellable.length + expired.length === 0) {
        return [product];
    }
    const taxPct = Number(product.tax_percentage ?? 0) || 0;
    const toRow = (batch, isExpired) => ({
        ...product,
        ...(Number(batch.sale_price ?? 0) > 0
            ? {
                  rate: Number(batch.sale_price),
                  tax_rate: taxPct > 0 ? (Number(batch.sale_price) * taxPct) / (100 + taxPct) : 0,
              }
            : {}),
        receiving_item_id: batch.receiving_item_id,
        batch_number: batch.batch_number,
        batch_expiry: batch.expiry_date,
        batch_expired: isExpired,
        // Units left in this batch; absent on a row cached before the
        // server sent it, and then no figure is shown.
        batch_qty: batch.real_qty ?? null,
        _rowKey: `${product.id}-b${batch.receiving_item_id}`,
    });
    // The stock outside every batch (a transfer in, a count gain), at the
    // product's price. A line is sold from one source, so this row is the only
    // way those units reach a sale.
    const unbatchedQty = Number(product.unbatched_qty);
    const unbatched =
        product.unbatched_qty != null && Number.isFinite(unbatchedQty) && unbatchedQty > 0
            ? [{ ...product, batch_unbatched: true, batch_qty: unbatchedQty, maxQty: Math.min(Number(product.maxQty) || 0, unbatchedQty) }]
            : [];
    return [...sellable.map((batch) => toRow(batch, false)), ...unbatched, ...expired.map((batch) => toRow(batch, true))];
};

/** A line's ceiling with its batch's cap applied, when it has one. */
const withBatchCap = (item, ceiling) => (Number.isFinite(Number(item?.batch_qty)) ? Math.min(ceiling, Number(item.batch_qty)) : ceiling);

/**
 * Is this search row already a line on the cart?
 *
 * A damaged unit is matched on its report alone, never on the variant: a
 * damaged unit and a normal unit of the same product are two different things
 * and belong on two different lines. Batch mode is the same idea one step on —
 * each batch is quoted at its own price, so another batch of a product already
 * on the cart is another line, and only the same batch twice is a duplicate.
 * The unbatched stock of a product is one more such line beside its batches.
 * With no batch on either side it is one line per product, as it always was.
 */
export const isSameCartLine = (item, product) => {
    if (product?.damaged_item_id != null) {
        return String(item?.damaged_item_id ?? '') === String(product.damaged_item_id);
    }
    if (item?.damaged_item_id || String(item?.id) !== String(product?.id)) return false;
    if (product?.receiving_item_id || item?.receiving_item_id) {
        return String(item?.receiving_item_id ?? '') === String(product?.receiving_item_id ?? '');
    }
    return true;
};

/**
 * Bring a restored cart line onto the ex-tax rate convention.
 *
 * `rate` used to be the tax-inclusive shelf price. A draft persisted before that
 * changed would be read as though it already excluded tax, quoting the customer
 * the shelf price plus tax again — an 18% over-charge on a cart nobody retyped.
 * `rate_excludes_tax` marks a line that has already been converted, so this runs
 * once per stale line and never again.
 */
const migrateRestoredItem = (item) => {
    if (!item || item.rate_excludes_tax) return item;

    return {
        ...item,
        rate: exclusiveRateFrom(item.rate ?? 0, Number(item.tax_percentage ?? 0) || 0, saleDecimals()),
        rate_excludes_tax: true,
    };
};

// Quantities are decimal(12,2) server-side, so a line may legitimately be 2.5 kg.
// Round to hundredths rather than flooring — flooring silently turned every
// fractional quantity into a smaller whole one.
const normalizeQuantity = (value) => Math.round(normalizeNonNegativeNumber(value) * 100) / 100;

/** RFC 4122 v4, shared with every other idempotent write. */
const createSaleRequestId = () => newClientRequestId();

const saleDraftFingerprint = (tab) => {
    const items = Array.isArray(tab?.items) ? tab.items : [];
    const form = tab?.form ?? {};
    const summary = calculateSaleSummary(items, pricedForm(form));

    return JSON.stringify({
        branchId: String(form.branch_id ?? ''),
        paymentBranchId: String(form.payment_branch_id ?? form.branch_id ?? ''),
        generalOrder: Boolean(form.general_order),
        customerId: String(tab?.customer?.id ?? ''),
        automotiveId: String(form.automotive_id ?? ''),
        // Normalised so 12 / '12' / 12.0 hash alike — retyping the same reading
        // must not invalidate an open payment.
        automotiveOdometer:
            form.service_record?.odometer === '' || form.service_record?.odometer == null
                ? ''
                : String(Math.trunc(Number(form.service_record.odometer))),
        date: String(form.date ?? ''),
        // Another currency, or another rate, is another basket.
        currency: String(form.currency_code ?? ''),
        exchangeRate: form.currency_code ? String(form.exchange_rate ?? '') : '',
        note: String(form.notes ?? ''),
        discountType: String(form.discount_type ?? ''),
        discountValue: roundRate(summary.discountValue, saleDecimals()),
        salesTaxPercentage: roundMoney(summary.salesTaxPercentage),
        fedTaxPercentage: roundMoney(summary.fedTaxPercentage),
        extraTaxPercentage: roundMoney(summary.extraTaxPercentage),
        // The same basket for a filer and a non-filer is a different total, so
        // it must not reuse an open payment's request id.
        advanceTaxRate: roundMoney(tab?.customer?.advance_tax?.rate ?? 0),
        items: items.map((item) => [
            String(item.id ?? ''),
            // A damaged unit is one report per unit, so two reports of the same
            // variant are two different lines and must not share a fingerprint.
            String(item.damaged_item_id ?? ''),
            normalizeQuantity(item.qty),
            // At the sale's places, as the line carries its rate.
            roundRate(item.rate, saleDecimals()),
            String(item.discount_type === 'percent' ? 'percent' : 'rs'),
            roundRate(item.discount ?? 0, saleDecimals()),
            // Attribution is part of the deal the payment modal was opened on:
            // swapping the worker must reopen it, not silently re-attribute.
            // All three overrides the till can type are here — the per-unit rate
            // is as much a change to what the worker earns as the total base is,
            // and leaving it out let one of the three slip past a reopen.
            String(item.worker_id ?? ''),
            item.worker_rate_override === null || item.worker_rate_override === undefined || item.worker_rate_override === ''
                ? ''
                : String(roundMoney(item.worker_rate_override)),
            item.worker_base_override === null || item.worker_base_override === undefined || item.worker_base_override === ''
                ? ''
                : String(roundMoney(item.worker_base_override)),
            item.worker_percentage === null || item.worker_percentage === undefined || item.worker_percentage === ''
                ? ''
                : String(roundMoney(item.worker_percentage)),
            // Batch mode: another batch is another basket. Appended only when
            // present, so a batch-free line fingerprints exactly as before.
            ...(item.receiving_item_id ? [String(item.receiving_item_id)] : []),
        ]),
    });
};

/**
 * Was the optional Job details panel actually used?
 *
 * An empty object must not be sent: the server rejects a service_record without
 * an automotive and would otherwise create an empty job card on every checkout.
 */
const hasServiceRecordInput = (record) => {
    if (!record || typeof record !== 'object') return false;

    return Object.entries(record).some(([key, value]) => {
        if (key === 'recommendations' || key === 'resolved_recommendation_ids') {
            return Array.isArray(value) && value.length > 0;
        }
        return value !== '' && value !== null && value !== undefined;
    });
};

/** Strip blanks so the request carries only what the cashier filled in. */
const compactServiceRecord = (record) => {
    const out = {};

    Object.entries(record ?? {}).forEach(([key, value]) => {
        if (key === 'recommendations') {
            const items = (Array.isArray(value) ? value : []).filter((item) => String(item?.description ?? '').trim() !== '');
            if (items.length) out.recommendations = items;
            return;
        }
        if (key === 'resolved_recommendation_ids') {
            const ids = (Array.isArray(value) ? value : []).map(Number).filter(Boolean);
            if (ids.length) out.resolved_recommendation_ids = ids;
            return;
        }
        if (value !== '' && value !== null && value !== undefined) out[key] = value;
    });

    return out;
};

/**
 * The buyer's standing discount (`customers.discount_percentage`), as a percent.
 *
 * Zero for a walk-in, and zero for a cached customer row written before the
 * column reached the offline payload — restrictive rather than wrong, and the
 * cashier can still type the rate in.
 */
const customerDiscountPercent = (tab) => {
    const percent = Number(tab?.customer?.discount_percentage ?? 0);
    if (!Number.isFinite(percent) || percent <= 0) return 0;
    return Math.min(percent, 100);
};

/**
 * Decide the automatic discount on one cart line.
 *
 * Two defaults compete for the Discount box: the figure the variant carries in
 * the catalogue (`branch_variants` / `product_variants`, kept here as
 * `base_discount`) and the customer's standing percentage. The buyer keeps
 * whichever is worth more on this line — compared in cents, because a rupee
 * figure and a percentage can only be weighed against each other at the
 * quantity actually being sold. That is also why this is re-run on every
 * quantity change: 200 PKR beats 10% on one unit and loses on three.
 *
 * A line the cashier has typed into is theirs from then on. `discount_source`
 * marks that the same way `furtherTaxAutoFilled` does for the header rate: this
 * is a default, not a lock.
 */
const withAutoLineDiscount = (item, customerPercent) => {
    if (item?.discount_source === 'manual') return item;
    // A damaged unit is already discounted to its damage price; the customer's
    // standing percentage must not stack on top of it automatically.
    if (item?.is_damaged_sale) return item;

    // A draft persisted before this existed carries no baseline, so whatever
    // discount it already holds is the catalogue figure by definition.
    const baseType = (item?.base_discount_type ?? item?.discount_type) === 'percent' ? 'percent' : 'rs';
    const baseValue = Number(item?.base_discount ?? item?.discount ?? 0) || 0;

    // Compared at the sale's scale, the one the server's own contest
    // (MultiSaleController::applyAutoDiscount) runs at, so a near-tie goes the
    // same way at the till and on a multi-sale.
    const catalogueUnits = lineDiscountUnits(
        {
            ...item,
            discount: baseValue,
            discount_type: baseType,
        },
        saleDecimals(),
    );
    const customerUnits = customerPercent > 0 ? percentOf(lineGrossUnits(item, saleDecimals()), toBasisPoints(customerPercent)) : 0;

    const useCustomer = customerUnits > catalogueUnits;

    return {
        ...item,
        base_discount: baseValue,
        base_discount_type: baseType,
        discount: useCustomer ? customerPercent : baseValue,
        discount_type: useCustomer ? 'percent' : baseType,
        discount_source: useCustomer ? 'customer' : 'catalogue',
    };
};

const isGeneralOrderBranch = (branchId) => branchId === null || branchId === undefined || branchId === '' || branchId === 'general';

const isMultiBranchAddonEnabled = () => {
    try {
        return Boolean(useConfigurationStore().isAddonEnabled('multi_branch_fulfillment'));
    } catch {
        return false;
    }
};

const isGeneralOrderTab = (tab) => {
    // Company-wide stock search / multi-branch cart mode. Initiation branch
    // (form.branch_id) stays set separately so the front office is always recorded.
    if (Boolean(tab?.form?.general_order) || tab?.form?.branch_id === 'general') {
        return isMultiBranchAddonEnabled();
    }
    return false;
};

const beginCustomerRequest = (tabId) => {
    const requestId = ++customerRequestSequence;
    latestCustomerRequestByTab.set(tabId, requestId);
    return requestId;
};

const invalidateCustomerRequest = (tabId) => {
    latestCustomerRequestByTab.set(tabId, ++customerRequestSequence);
};

const isLatestCustomerRequest = (tabId, requestId) => latestCustomerRequestByTab.get(tabId) === requestId;

const queuePersist = (fn) => {
    const generation = persistGeneration;
    clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
        persistTimer = null;
        persistChain = persistChain
            .then(() => (generation === persistGeneration ? fn() : undefined))
            .catch((err) => console.error('IDB persist error:', err));
    }, 250);
};

const snapshotStore = (store) => ({
    // Pinia only unwraps the root with toRaw(); nested tabs/items can still be
    // Vue proxies, which native structuredClone rejects with DataCloneError.
    // POS draft state is JSON-shaped, so JSON serialization is the reliable
    // deep de-proxy operation here.
    tabs: JSON.parse(JSON.stringify(toRaw(store.tabs))),
    activeTabId: store.activeTabId,
    tabCounter: store.tabCounter,
});

/**
 * A persisted tab back into the store. A persisted request cannot still be in
 * flight after a browser restart: keep its payload for verification/retry, but
 * never restore the transient submission lock.
 */
const restoreTab = (tab) => ({
    ...tab,
    isSubmitting: false,
    items: Array.isArray(tab?.items) ? tab.items.map(migrateRestoredItem) : tab?.items,
});

/** Something a cashier would miss: a line, a customer, a note, a sale mid-payment. */
const tabHasContent = (tab) =>
    Boolean((tab?.items || []).length || tab?.customer?.id || tab?.selectedCustomerPill || tab?.form?.notes || tab?.pendingSalePayload);

let stopPeerReservationListener = null;
let peerSessionStore = null;

/**
 * Wire a store instance to the other tabs of this browser: a peer's holds
 * moving re-derives the caps here, and a tab coming back into view checks
 * that its carts and holds are still its own. The latest instance wins, so a
 * store rebuilt after a session reset does not leave a stale one listening.
 */
const listenForPeerSessions = (store) => {
    const first = peerSessionStore === null;
    peerSessionStore = store;
    stopPeerReservationListener?.();
    stopPeerReservationListener = subscribePosReservationChanges((event) => {
        peerSessionStore?.onPeerReservationChange(event).catch((err) => console.error('Peer reservation refresh failed:', err));
    });
    if (!first || typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        peerSessionStore
            ?.resumeReservations()
            .then((outcome) => {
                // The carts on screen belong to another tab now; a fresh page
                // load starts empty, and the strip follows the store.
                if (outcome === 'adopted') window.location.reload();
            })
            .catch((err) => console.error('Reservation resume failed:', err));
    });
};

/**
 * The till's one search box, answered from the offline cache.
 *
 * Substring rather than the server's prefix match: this is a scan of a local
 * table, and a superset of what the server would return can only ever help the
 * cashier — it never hides a customer the network leg would have found. Phone
 * matching is unchanged from when this only searched phones.
 */
const findCachedCustomers = async (term) => {
    if (!offlineDB.customers) return [];
    const query = String(term ?? '')
        .trim()
        .toLowerCase();
    if (!query) return [];
    const contains = (value) =>
        String(value ?? '')
            .toLowerCase()
            .includes(query);

    // Indexed answers first. What a cashier types at the customer box is nearly
    // always a whole phone number or an id, and both of those are prefix matches
    // from position zero — which the `phone` index and the primary key answer
    // without touching another row. The substring scan below is a full cursor
    // walk of every cached customer, and at twenty-five thousand rows it was
    // costing a fifth of a second on every Enter.
    const byIndex = new Map();
    const remember = (rows) => {
        for (const row of rows) if (row?.id != null) byIndex.set(String(row.id), row);
    };

    if (/^\d+$/.test(query)) {
        remember(await offlineDB.customers.where('phone').startsWith(query).limit(20).toArray());
        const byId = await offlineDB.customers.get(Number(query));
        if (byId) remember([byId]);
        if (byIndex.size >= 20) return [...byIndex.values()].slice(0, 20);
    }
    remember(await offlineDB.customers.where('name').startsWithIgnoreCase(query).limit(20).toArray());
    if (byIndex.size >= 20) return [...byIndex.values()].slice(0, 20);

    // Fallback: a mid-string match — a surname, part of a company name, an
    // internal code fragment. No index can serve it, so it still scans, but only
    // when the cheap paths came back short.
    const scanned = await offlineDB.customers
        .filter(
            (customer) =>
                contains(customer?.phone) || contains(customer?.name) || contains(customer?.internal_code) || String(customer?.id ?? '') === query,
        )
        .limit(20)
        .toArray();
    remember(scanned);

    return [...byIndex.values()].slice(0, 20);
};

const lookupCustomers = async (term) => {
    if (canMutateOnline()) {
        try {
            const response = await apiClient.post('/customer/fetchCustomer', { search: term }, { offlinePolicy: 'read' });
            return {
                customers: response.data?.data || [],
                authoritative: true,
            };
        } catch (error) {
            const cached = await findCachedCustomers(term);
            if (cached.length) {
                return { customers: cached, authoritative: false };
            }
            throw error;
        }
    }

    return {
        customers: await findCachedCustomers(term),
        authoritative: false,
    };
};

/**
 * Does this term look like the phone number of a customer who does not exist yet?
 *
 * Only then is offering to register one the right answer to an empty result: a
 * name, an internal code or an id typed at the till is a lookup that missed, and
 * prefilling the registration form's phone field with "Ali Khan" is not a
 * customer record anybody wants.
 */
const isPhoneTerm = (term) => /^\d{11}$/.test(String(term ?? '').trim());

/** What to show on the selected-customer pill for a buyer found by name or code. */
const customerPillLabel = (customer) => customer?.phone || customer?.name || customer?.internal_code || `CUS-${customer?.id ?? ''}`;

export const useSaleStore = defineStore('sale', {
    state: () => ({
        tabs: {},
        activeTabId: null,
        _skipNextSync: false,
        tabCounter: 0,
        paymentModal: false,
        paymentTabId: null,

        livePayment: {
            method: 'cash',
            amount: '',
            transaction_id: '',
            selectedAccount: null,
            activeModalTab: 'payment',
            creditChangeToWallet: false,
            walletCreditAmount: 0,
            // A split checkout's legs and totals (empty for one payment).
            tenders: [],
            splitPaid: 0,
            splitRemaining: 0,
            splitChange: 0,
        },
        liveDelivery: {
            name: '',
            phone: '',
            address: '',
        },
        // The multi-sale order each tab has on screen, by tab id. The order
        // lives on the server and in the tab's own view; this copy exists so
        // the customer display can show it instead of the tab's hidden cart.
        // Never persisted: an open order is live stock, re-read on attach.
        multiSaleOrders: {},
        availableBranches: [],

        payment: {
            amount: 0,
            method: 'cash',
        },

        saleSubmitting: false,
        lastCreatedSaleId: null,

        // Set when the server refuses a sale on credit grounds. The POS reads the
        // figures out of here to reopen its approval dialog, then clears it.
        creditLimitRejection: null,
        // Set when the server refuses a card or online tender for want of an
        // account (ACCOUNT_REQUIRED: no card machine resolved at the payment
        // branch). The POS puts the account select in front of the cashier
        // with the server's words, then clears it.
        accountRequiredRejection: null,
    }),

    getters: {
        currentTab: (state) => state.tabs[state.activeTabId] || null,
        form: (state) => state.tabs[state.activeTabId]?.form || {},
        items: (state) => state.tabs[state.activeTabId]?.items || [],
        customer: (state) => state.tabs[state.activeTabId]?.customer || {},
        selectedCustomerPill: (state) => state.tabs[state.activeTabId]?.selectedCustomerPill || '',
        matchingCustomers: (state) => state.tabs[state.activeTabId]?.matchingCustomers || [],
        showPhoneDropdown: (state) => state.tabs[state.activeTabId]?.showPhoneDropdown || false,
        showRegisterModal: (state) => state.tabs[state.activeTabId]?.showRegisterModal || false,
        newCustomerPhone: (state) => state.tabs[state.activeTabId]?.newCustomerPhone || '',
        showDeleteModal: (state) => state.tabs[state.activeTabId]?.showDeleteModal || false,
        deleteIndex: (state) => state.tabs[state.activeTabId]?.deleteIndex ?? null,
        checkedPhoneNumbers: (state) => state.tabs[state.activeTabId]?.checkedPhoneNumbers || [],
        isSubmitting: (state) => state.tabs[state.activeTabId]?.isSubmitting || false,
        searchTerm: (state) => state.tabs[state.activeTabId]?.searchTerm || '',
        apiItems: (state) => state.tabs[state.activeTabId]?.apiItems || [],
        showProductDropdown: (state) => state.tabs[state.activeTabId]?.showProductDropdown || false,

        grossAmount(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab) return 0;
            return calculateSaleSummary(tab.items, pricedForm(tab.form)).grossAmount;
        },
        totalDiscountAmount(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab) return 0;
            return calculateSaleSummary(tab.items, pricedForm(tab.form)).discountAmount;
        },
        taxAmount(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab) return 0;
            return calculateSaleSummary(tab.items, pricedForm(tab.form)).taxAmount;
        },
        netAmount(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab) return 0;
            return calculateSaleSummary(tab.items, pricedForm(tab.form)).netAmount;
        },
        finalTotal(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab) return 0;
            return calculateSaleSummary(tab.items, pricedForm(tab.form)).finalTotal;
        },
        cityCountry(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab || (!tab.customer.city && !tab.customer.country)) return '—';
            return `${tab.customer.city ?? ''} | ${tab.customer.country ?? ''}`;
        },
        allTabIds: (state) => Object.keys(state.tabs),
        /** Whether any of this session's carts holds a line — every tab, not just the one on screen. */
        hasCartItems: (state) => Object.values(state.tabs).some((tab) => (tab?.items?.length ?? 0) > 0),
    },

    actions: {
        // ── IDB persistence ───────────────────────────────────────

        async loadFromIDB() {
            const generation = persistGeneration;
            try {
                listenForPeerSessions(this);
                // This tab's own drafts — a reload finds them, a new tab does
                // not. Carts left behind by a closed tab are picked up when the
                // POS screen mounts (adoptOrphanedDrafts), not here.
                const saved = await loadStoreState(getPosSessionId());
                if (generation !== persistGeneration) return;
                if (saved && saved.tabs) {
                    this.tabs = Object.fromEntries(Object.entries(saved.tabs).map(([id, tab]) => [id, restoreTab(tab)]));
                    this.activeTabId = saved.activeTabId ?? null;
                    this.tabCounter = saved.tabCounter ?? 0;
                }

                // Rebuild the aggregate reservation counters. This session's
                // rows from a previous page load are stale whether or not it
                // restored carts — a crash can leave quantities and
                // branch_products.reserved_qty out of sync — and the peers'
                // rows are re-summed on the way.
                reservationGeneration++;
                await resetAllReservations();
                for (const branchId of this._reservedBranchIds()) {
                    await this._refreshAllTabsMaxQty(branchId);
                }
            } catch (err) {
                console.error('IDB load error:', err);
            }
        },

        /** Branches this tab's carts are pinned to, each once. */
        _reservedBranchIds() {
            return [
                ...new Set(
                    Object.values(this.tabs)
                        .map((tab) => tab?.form?.branch_id)
                        .filter((branchId) => branchId !== null && branchId !== undefined && branchId !== ''),
                ),
            ];
        },

        /**
         * Take over the drafts of sessions that are gone: a tab that closed
         * (its `pagehide` said so), one silent for POS_SESSION_ADOPT_MS, or the
         * single `global` document every tab shared before schema v36. Called
         * when the POS screen mounts rather than at boot, so a cart is picked
         * up by the tab that shows the till and not by whichever tab of the
         * app happened to load first. Each adopted session row is replaced by
         * a marker naming this session, so a tab that was merely frozen and
         * comes back finds out its carts moved (resumeReservations) instead of
         * selling the same units a second time.
         */
        async adoptOrphanedDrafts() {
            if (!offlineDB.sale_drafts) return [];
            const sessionId = getPosSessionId();
            const now = Date.now();

            const orphans = await offlineDB.transaction('rw', offlineDB.sale_drafts, async () => {
                const rows = await offlineDB.sale_drafts.toArray();
                const taken = [];
                for (const row of rows) {
                    const owner = String(row?.tabId ?? '');
                    if (!owner || owner === sessionId) continue;
                    if (row.adopted_by) {
                        // A marker nobody came back for.
                        if (now - (Date.parse(row.adopted_at ?? '') || 0) > POS_SESSION_ADOPT_MS) {
                            await offlineDB.sale_drafts.delete(owner);
                        }
                        continue;
                    }
                    if (!row.data?.tabs) continue;
                    if (owner !== 'global' && !isPosSessionAdoptable(owner, { now })) continue;
                    taken.push(row);
                    if (owner === 'global') {
                        await offlineDB.sale_drafts.delete(owner);
                    } else {
                        await offlineDB.sale_drafts.put({ tabId: owner, adopted_by: sessionId, adopted_at: new Date(now).toISOString() });
                    }
                }
                return taken;
            });

            const added = [];
            for (const row of orphans) {
                for (const [id, tab] of Object.entries(row.data.tabs)) {
                    // A blank tab is not a sale; the strip makes its own.
                    if (this.tabs[id] || !tabHasContent(tab)) continue;
                    this.tabs[id] = restoreTab(tab);
                    added.push(id);
                }
                this.tabCounter = Math.max(this.tabCounter, Number(row.data.tabCounter) || 0);
            }
            if (!added.length) return [];

            if (!this.activeTabId || !this.tabs[this.activeTabId]) this.activeTabId = added[0];
            reservationGeneration++;
            // Theirs until this write lands; a tab closing in between would lose them.
            await this.flushPersistToIDB();
            for (const branchId of this._reservedBranchIds()) {
                await this._refreshAllTabsMaxQty(branchId);
            }
            return added;
        },

        /**
         * The tab is visible again. Peers drop a silent session's rows, and
         * this reconcile is what puts them back — but if the tab was away long
         * enough for another POS screen to adopt its drafts, the carts on
         * screen belong to that tab now and a copy here would sell the same
         * units twice, so it starts over instead.
         */
        async resumeReservations() {
            if (!Object.keys(this.tabs).length) return 'empty';

            if (offlineDB.sale_drafts) {
                const sessionId = getPosSessionId();
                const row = await offlineDB.sale_drafts.get(sessionId);
                if (row?.adopted_by && String(row.adopted_by) !== sessionId) {
                    this.resetLocalSessionState();
                    await offlineDB.sale_drafts.delete(sessionId);
                    return 'adopted';
                }
            }

            reservationGeneration++;
            for (const branchId of this._reservedBranchIds()) {
                await this._refreshAllTabsMaxQty(branchId);
            }
            return 'held';
        },

        /** Another tab's holds at `event.branchId` moved; re-derive this tab's caps there. */
        async onPeerReservationChange(event) {
            const branchKey = String(event?.branchId ?? '');
            if (!branchKey) return;
            if (!this._reservedBranchIds().some((branchId) => String(branchId) === branchKey)) return;
            reservationGeneration++;
            await this._refreshAllTabsMaxQty(branchKey);
        },

        persistToIDB() {
            const cacheWriteSnapshot = captureOfflineCacheWriteEpoch({ requireOwner: true });
            const snapshot = snapshotStore(this);
            queuePersist(async () => {
                await saveStoreState(snapshot, cacheWriteSnapshot);
            });
        },

        async flushPersistToIDB() {
            clearTimeout(persistTimer);
            persistTimer = null;
            const generation = persistGeneration;
            const cacheWriteSnapshot = captureOfflineCacheWriteEpoch({ requireOwner: true });
            const snapshot = snapshotStore(this);
            persistChain = persistChain
                .then(() => (generation === persistGeneration ? saveStoreState(snapshot, cacheWriteSnapshot) : undefined))
                .catch((err) => console.error('IDB persist error:', err));
            await persistChain;
        },

        getFreshTabData() {
            return {
                customer: {},
                selectedCustomerPill: '',
                form: {
                    id: '',
                    customer_id: '',
                    phone: '',
                    name: '',
                    discount: 0,
                    discount_type: 'percent',
                    salesTax: 0,
                    notes: '',
                    branch_id: null,
                    payment_branch_id: null,
                    // When multi-branch is on, fresh carts search company stock by default
                    // while initiation branch_id is filled separately on the POS.
                    general_order: isMultiBranchAddonEnabled(),
                    date: todayLocal(),
                    // Automotive addon. Absent on drafts persisted before the
                    // upgrade, so every read must tolerate undefined.
                    automotive_id: '',
                    automotive_label: '',
                    service_record: null,
                    // The sale's currency (null = base) and its rate to base.
                    currency_code: null,
                    exchange_rate: '',
                },
                items: [],
                searchTerm: '',
                apiItems: [],
                showProductDropdown: false,
                matchingCustomers: [],
                showPhoneDropdown: false,
                showRegisterModal: false,
                newCustomerPhone: '',
                showDeleteModal: false,
                deleteIndex: null,
                checkedPhoneNumbers: [],
                isSubmitting: false,

                pendingSalePayload: null,
            };
        },

        _setTab(tabId, data) {
            this.tabs[tabId] = data;
            this.persistToIDB();
        },

        initTab(tabId) {
            if (!this.tabs[tabId]) {
                this.tabs[tabId] = this.getFreshTabData();
            }
            this.activeTabId = tabId;
            this.persistToIDB();
        },

        setActiveTab(tabId) {
            if (this.tabs[tabId]) {
                this.activeTabId = tabId;
                this.persistToIDB();
            }
        },

        createNewTab() {
            this.tabCounter++;
            const tabId = `tab-${this.tabCounter}`;
            this._setTab(tabId, this.getFreshTabData());
            this.activeTabId = tabId;
            this.persistToIDB();
            return tabId;
        },

        switchTab(tabId) {
            if (!this.tabs[tabId]) return;
            this.activeTabId = tabId;
            this.persistToIDB();
        },

        async removeTab(tabId) {
            const branchId = this.tabs[tabId]?.form?.branch_id;
            invalidateCustomerRequest(tabId);
            delete this.tabs[tabId];
            if (this.paymentTabId === tabId) {
                this.paymentTabId = null;
                this.paymentModal = false;
            }
            if (this.activeTabId === tabId) {
                const remaining = Object.keys(this.tabs);
                this.activeTabId = remaining.length > 0 ? remaining[0] : null;
            }
            reservationGeneration++;
            this.persistToIDB();
            if (branchId !== null && branchId !== undefined && branchId !== '') {
                await this._refreshAllTabsMaxQty(branchId);
            }
        },

        async clearTab(tabId) {
            if (this.tabs[tabId]) {
                const branchId = this.tabs[tabId]?.form?.branch_id;
                invalidateCustomerRequest(tabId);
                this._setTab(tabId, this.getFreshTabData());
                if (this.paymentTabId === tabId) {
                    this.paymentTabId = null;
                    this.paymentModal = false;
                }
                reservationGeneration++;
                if (branchId !== null && branchId !== undefined && branchId !== '') {
                    await this._refreshAllTabsMaxQty(branchId);
                }
            }
        },

        async clearTabItems(tabId, reservationBranchId = this.tabs[tabId]?.form?.branch_id) {
            const tab = this.tabs[tabId];
            if (!tab) return;
            const currentBranchId = tab.form?.branch_id;
            this._setTab(tabId, {
                ...tab,
                items: [],
                pendingSalePayload: null,
            });
            reservationGeneration++;
            const affectedBranches = new Set(
                [reservationBranchId, currentBranchId].filter((branchId) => branchId !== null && branchId !== undefined && branchId !== ''),
            );
            for (const branchId of affectedBranches) {
                await this._refreshAllTabsMaxQty(branchId);
            }
        },

        /**
         * Empty every cart for a switch of the POS pricing mode.
         *
         * A line's rate is frozen when it is added and travels to the server as
         * its `amount`, and in batch mode the line also carries the batch it was
         * picked from — so a cart built under the old mode would keep charging
         * the old mode's prices. The cached search rows go too, because each one
         * is a price (and in batch mode a batch) resolved by the old mode. The
         * customer, branch and discounts stay: only what was priced is dropped.
         *
         * Flushed before it returns, so the emptied carts are on disk before
         * the new mode can reach the till.
         */
        async clearCartsForPricingModeChange() {
            for (const tabId of Object.keys(this.tabs)) {
                const tab = this.tabs[tabId];
                if (!tab) continue;
                this.tabs[tabId] = {
                    ...tab,
                    searchTerm: '',
                    apiItems: [],
                    showProductDropdown: false,
                    showDeleteModal: false,
                    deleteIndex: null,
                };
                if (tab.items?.length || tab.pendingSalePayload) {
                    await this.clearTabItems(tabId);
                }
            }
            await this.flushPersistToIDB();
        },

        /**
         * Drop identity-bound Pinia state without touching shared IndexedDB.
         * Used by other tabs when one tab owns the database cleanup.
         */
        resetLocalSessionState() {
            persistGeneration++;
            reservationGeneration++;
            latestCustomerRequestByTab.clear();
            clearTimeout(persistTimer);
            persistTimer = null;
            this.$reset();
        },

        async clearAll() {
            this.resetLocalSessionState();
            await reservationChain.catch(() => {});
            await persistChain;
            await clearStoreState();
        },

        async resetCurrentTab() {
            const tabId = this.activeTabId;
            if (!this.tabs[tabId]) return;
            await this.clearTab(tabId);
        },

        updateFormField(field, value) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            const previousBranchId = tab.form?.branch_id;
            const branchChanged = field === 'branch_id' && String(previousBranchId ?? '') !== String(value ?? '');
            if (branchChanged && !canMutateOnline()) {
                useToast().displayToast({
                    message: 'Reconnect before changing the sale branch.',
                    variant: 'warning',
                });
                return;
            }
            if (field === 'phone' && value !== tab.form?.phone) {
                invalidateCustomerRequest(tabId);
            }

            const nextForm = { ...tab.form, [field]: value };
            const previousGeneralOrder = Boolean(tab.form?.general_order);
            let stockScopeChanged = false;

            // Initiation / front-office branch — always a real branch id.
            // The cash till follows it.
            if (field === 'branch_id') {
                if (isGeneralOrderBranch(value)) {
                    useToast().displayToast({
                        message: 'Select the front-office branch where this order is initiated.',
                        variant: 'warning',
                    });
                    return;
                }
                nextForm.branch_id = Number(value);
                nextForm.payment_branch_id = nextForm.branch_id;
            }

            // Company-wide stock search scope (independent of initiation branch).
            if (field === 'general_order') {
                if (value && !isMultiBranchAddonEnabled()) {
                    nextForm.general_order = false;
                } else {
                    nextForm.general_order = Boolean(value);
                }
                stockScopeChanged = previousGeneralOrder !== Boolean(nextForm.general_order);
                nextForm.payment_branch_id = nextForm.branch_id ?? null;
            }

            // Only a change that can invalidate a line already on the cart may
            // discard it. Widening the stock search to the company pool cannot:
            // every branch that could source a line before can still source it.
            // Narrowing it back to the order-from branch can, because lines may
            // have been picked from stock this branch does not hold.
            const narrowedStockScope = stockScopeChanged && !nextForm.general_order;
            // A company-wide cart is not sourced from the order-from branch at
            // all — that branch is the front office — so moving it leaves every
            // line valid and the cart stands.
            const branchChangeInvalidates = branchChanged && !nextForm.general_order;
            const shouldClearCart = (branchChangeInvalidates || narrowedStockScope) && tab.items.length > 0;

            this._setTab(tabId, {
                ...tab,
                form: nextForm,
                ...(shouldClearCart
                    ? {
                          items: [],
                          pendingSalePayload: null,
                      }
                    : {}),
            });

            if (branchChanged || stockScopeChanged) {
                reservationGeneration++;
                if (shouldClearCart) {
                    useToast().displayToast({
                        message: branchChanged ? 'Order-from branch changed — cart cleared.' : 'Stock search scope changed — cart cleared.',
                        variant: 'warning',
                    });
                }
                if (!isGeneralOrderBranch(previousBranchId) && !previousGeneralOrder) {
                    void this._refreshAllTabsMaxQty(previousBranchId).catch(() => {});
                }
                if (!isGeneralOrderBranch(nextForm.branch_id) && !nextForm.general_order) {
                    void this._refreshAllTabsMaxQty(nextForm.branch_id).catch(() => {});
                }
            }
        },

        updateTabData(field, value) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            this._setTab(tabId, { ...tab, [field]: value });
            if (field === 'items') {
                reservationGeneration++;
                const branchId = tab.form?.branch_id;
                if (!isGeneralOrderBranch(branchId)) {
                    void this._refreshAllTabsMaxQty(branchId).catch(() => {});
                }
            }
        },

        /**
         * Push the buyer's standing discount onto every line it still owns.
         *
         * Run whenever the attached customer changes, so detaching one hands the
         * lines back to their catalogue figures rather than leaving the departed
         * buyer's rate priced into the sale.
         */
        applyCustomerDiscount(tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab?.items?.length) return;

            const percent = customerDiscountPercent(tab);
            const items = tab.items.map((item) => withAutoLineDiscount(item, percent));
            const changed = items.some((item, index) => {
                const before = tab.items[index];
                return (
                    Number(before.discount ?? 0) !== Number(item.discount ?? 0) ||
                    before.discount_type !== item.discount_type ||
                    before.discount_source !== item.discount_source ||
                    before.base_discount !== item.base_discount ||
                    before.base_discount_type !== item.base_discount_type
                );
            });
            if (!changed) return;

            this._setTab(tabId, { ...tab, items });
        },

        clearCustomerSelection() {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            invalidateCustomerRequest(tabId);
            this._setTab(tabId, {
                ...tab,
                selectedCustomerPill: '',
                customer: {},
                checkedPhoneNumbers: [],
                // The automotive belongs to the customer, so it can never outlive them.
                form: {
                    ...tab.form,
                    id: '',
                    phone: '',
                    name: '',
                    customer_id: '',
                    automotive_id: '',
                    automotive_label: '',
                    service_record: null,
                },
            });
            this.applyCustomerDiscount(tabId);
        },

        /**
         * Resolve whatever the cashier typed into the till's customer box.
         *
         * The term may be a phone number, a name, an internal code or the
         * customer id — the server ORs across all four. Registration is still
         * only offered for a phone number, since that is the one shape the
         * register modal can prefill from.
         */
        async searchAndShowDropdown(term) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            const requestId = beginCustomerRequest(tabId);
            try {
                const { customers, authoritative } = await lookupCustomers(term);
                if (!isLatestCustomerRequest(tabId, requestId) || !this.tabs[tabId] || String(this.tabs[tabId].form?.phone ?? '') !== String(term)) {
                    return;
                }
                const currentTab = this.tabs[tabId];
                if (customers.length > 0) {
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: customers,
                        showPhoneDropdown: true,
                    });
                } else if (authoritative && isPhoneTerm(term)) {
                    const newChecked = [...currentTab.checkedPhoneNumbers];
                    if (!newChecked.includes(term)) newChecked.push(term);
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: [],
                        showPhoneDropdown: false,
                        checkedPhoneNumbers: newChecked,
                        newCustomerPhone: term,
                        showRegisterModal: true,
                    });
                } else if (authoritative) {
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: [],
                        showPhoneDropdown: false,
                    });
                    useToast().displayToast({
                        message: `No customer matches "${term}". Search by phone to register a new one.`,
                        variant: 'warning',
                    });
                } else {
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: [],
                        showPhoneDropdown: false,
                    });
                    useToast().displayToast({
                        message: 'Customer is not in the offline cache. Reconnect to search or register.',
                        variant: 'warning',
                    });
                }
            } catch (err) {
                console.error('searchAndShowDropdown error:', err);
                // A failed lookup is not evidence that the customer does not
                // exist. Do not offer registration based on a network error.
            }
        },

        async onPhoneInput(phone) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            invalidateCustomerRequest(tabId);
            this._setTab(tabId, {
                ...tab,
                form: { ...tab.form, phone },
                matchingCustomers: [],
                showPhoneDropdown: false,
            });
            if (phone.length < 3) return;
            if (phone.length >= 3 && phone.length < 11) {
                const requestId = beginCustomerRequest(tabId);
                try {
                    const { customers } = await lookupCustomers(phone);
                    if (
                        !isLatestCustomerRequest(tabId, requestId) ||
                        !this.tabs[tabId] ||
                        String(this.tabs[tabId].form?.phone ?? '') !== String(phone)
                    ) {
                        return;
                    }
                    const currentTab = this.tabs[tabId];
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: customers,
                        showPhoneDropdown: customers.length > 0,
                    });
                } catch (err) {
                    console.error('Phone search error:', err);
                }
                return;
            }
            if (phone.length === 11) {
                await this.resolvePhone(phone, tabId);
            }
        },

        async resolvePhone(phone, tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab) return;
            if (tab.selectedCustomerPill === phone) return;
            if (tab.checkedPhoneNumbers.includes(phone)) return;
            const requestId = beginCustomerRequest(tabId);
            try {
                const { customers, authoritative } = await lookupCustomers(phone);
                if (!isLatestCustomerRequest(tabId, requestId) || !this.tabs[tabId] || String(this.tabs[tabId].form?.phone ?? '') !== String(phone)) {
                    return;
                }
                const currentTab = this.tabs[tabId];
                const newChecked = [...(currentTab.checkedPhoneNumbers || [])];
                if (!newChecked.includes(phone)) newChecked.push(phone);
                if (customers.length > 0) {
                    this.selectCustomerFromDropdown(customers[0], tabId);
                } else if (authoritative) {
                    this._setTab(tabId, {
                        ...currentTab,
                        checkedPhoneNumbers: newChecked,
                        newCustomerPhone: phone,
                        showRegisterModal: true,
                        showPhoneDropdown: false,
                        matchingCustomers: [],
                    });
                } else {
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: [],
                        showPhoneDropdown: false,
                    });
                    useToast().displayToast({
                        message: 'Customer is not in the offline cache. Reconnect to search or register.',
                        variant: 'warning',
                    });
                }
            } catch (err) {
                console.error('resolvePhone error:', err);
            }
        },

        selectCustomerFromDropdown(cust, tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab) return;
            invalidateCustomerRequest(tabId);
            const newChecked = [...tab.checkedPhoneNumbers];
            if (!newChecked.includes(cust.phone)) newChecked.push(cust.phone);
            this._setTab(tabId, {
                ...tab,
                customer: JSON.parse(JSON.stringify(cust)),
                form: {
                    ...tab.form,
                    id: cust.id,
                    customer_id: cust.id || null,
                    phone: cust.phone,
                    name: cust.name,
                    // A previous customer's automotive must never survive a
                    // customer switch.
                    automotive_id: '',
                    automotive_label: '',
                    service_record: null,
                },
                // Falls back to name / code / id: a customer found by name may
                // have no phone at all, and an empty pill reads as "nobody
                // attached" while the sale is in fact bound to them.
                selectedCustomerPill: customerPillLabel(cust),
                matchingCustomers: [],
                showPhoneDropdown: false,
                checkedPhoneNumbers: newChecked,
            });
            this.applyCustomerDiscount(tabId);
            // A buyer with a default currency opens the sale in it — only while
            // nothing is priced yet and the cashier has not chosen one, so a
            // customer change never re-prices a cart. Never in multi-sale mode,
            // whose orders are base-only. docs/multi-currency-plan.md
            const configStore = useConfigurationStore();
            const current = this.tabs[tabId];
            if (
                cust.currency_code &&
                configStore.multiCurrencyEnabled &&
                !configStore.multiSaleEnabled &&
                cust.currency_code !== configStore.baseCurrency &&
                !current?.form?.currency_code &&
                !(current?.items || []).length
            ) {
                const rate = configStore.currencyRate(cust.currency_code);
                if (rate) this.setTabCurrency(tabId, cust.currency_code, rate);
            }
        },

        closeRegisterModal(tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab) return;
            invalidateCustomerRequest(tabId);
            this._setTab(tabId, { ...tab, showRegisterModal: false, newCustomerPhone: '' });
        },

        /**
         * The register modal hands back the row it saved. Re-fetch by its id
         * rather than by the phone typed at the till: the full form lets the
         * operator correct the number on the way through, and an id lookup also
         * finds a customer saved with no phone at all.
         */
        async handleCustomerCreated(customer = null) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            const lookup = customer?.id ? { id: customer.id } : { phone: customer?.phone || tab.newCustomerPhone };
            const requestId = beginCustomerRequest(tabId);
            await new Promise((r) => setTimeout(r, 500));
            if (!isLatestCustomerRequest(tabId, requestId) || !this.tabs[tabId]) return;
            try {
                const res = await apiClient.post('/customer/fetchCustomer', lookup, { offlinePolicy: 'read' });
                if (!isLatestCustomerRequest(tabId, requestId) || !this.tabs[tabId]) return;
                if (res.data?.success && res.data.data?.length) {
                    const newCust = res.data.data[0];
                    const currentTab = this.tabs[tabId];
                    const newChecked = [...currentTab.checkedPhoneNumbers];
                    if (!newChecked.includes(newCust.phone)) newChecked.push(newCust.phone);
                    this._setTab(tabId, {
                        ...currentTab,
                        customer: JSON.parse(JSON.stringify(newCust)),
                        form: {
                            ...currentTab.form,
                            id: newCust.id,
                            customer_id: newCust.id || null,
                            phone: newCust.phone,
                            name: newCust.name,
                            // A brand-new customer owns nothing yet.
                            automotive_id: '',
                            automotive_label: '',
                            service_record: null,
                        },
                        selectedCustomerPill: customerPillLabel(newCust),
                        checkedPhoneNumbers: newChecked,
                        showRegisterModal: false,
                        newCustomerPhone: '',
                    });
                    this.applyCustomerDiscount(tabId);
                }
            } catch (err) {
                console.error('handleCustomerCreated error:', err);
                useToast().displayToast({
                    message: 'Customer created but failed to load. Please search manually',
                    variant: 'error',
                });
            }
            if (isLatestCustomerRequest(tabId, requestId)) {
                this.closeRegisterModal(tabId);
            }
        },

        async addItemToSale(product) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;

            const branchId = tab.form.branch_id;
            const isGeneral = isGeneralOrderTab(tab);
            const lineDiscountType = product.discount_type === 'percent' ? 'percent' : 'rs';
            const lineDiscount = Number(product.discount ?? 0) || 0;

            // A damaged report is a batch of units at an agreed price, sold in
            // as many checkouts as it takes to clear. `damaged_item_id` marks a
            // candidate row, the rate is the frozen damage price (tax-inclusive
            // like the catalogue), and the line may run up to whatever is left
            // of the report.
            //
            // Matched on the report id alone, never on the variant: a damaged
            // unit and a normal unit of the same product are two different
            // things and belong on two different lines.
            if (product.damaged_item_id != null) {
                if (tab.items.some((item) => isSameCartLine(item, product))) return;
                const resolvedTaxPct = Number(product.tax_percentage ?? 0) || 0;
                // The ceiling is what the report has left; the line opens at one
                // unit. It used to open at the whole remainder, so picking a
                // 300-unit report put 300 units in the cart and one Enter sold
                // the entire batch.
                const remainingQty = Math.max(0, normalizeQuantity(product.remaining_quantity ?? product.maxQty ?? 1));
                const openingQty = Math.min(normalizeQuantity(product.quantity ?? 1) || 1, remainingQty);
                const newItems = [
                    ...tab.items,
                    {
                        id: product.id,
                        name: product.name,
                        qty: openingQty,
                        rate: cartRateFrom(product, resolvedTaxPct),
                        // As above: already ex-tax, so the restore migration skips it.
                        rate_excludes_tax: true,
                        maxQty: remainingQty,
                        _lastQty: 0,
                        damaged_item_id: product.damaged_item_id,
                        is_damaged_sale: true,
                        // The report's remaining quantity is the sale cap, and it
                        // is capped again server-side under the report's row lock.
                        damage_qty: remainingQty,
                        damage_price: Number(product.damage_price ?? 0) || 0,
                        shelf_price: Number(product.shelf_price ?? 0) || 0,
                        tax_rate: product.tax_rate ?? 0,
                        tax_percentage: resolvedTaxPct,
                        tax_mode: product.tax_mode ?? 'inclusive',
                        // Same snapshot decisions the checkout freezes by.
                        is_third_schedule: Boolean(product.third_schedule),
                        mrp: Number(product.mrp ?? 0) > 0 ? Number(product.mrp) : null,
                        supply_type: product.supply_type ?? 'standard',
                        fed_excisable: Boolean(product.fed_excisable),
                        unit_abbr: product.unit_abbr || '',
                        ...lineDetailFieldsFrom(product),
                        // The unit of measure decides, exactly as it does for a
                        // catalogue line: a damaged 0.4 kg is as sellable as an
                        // undamaged one, and the server holds the same rule.
                        allows_fractional: Boolean(product.allows_fractional),
                        base_discount: 0,
                        base_discount_type: 'rs',
                        discount_source: 'catalogue',
                        discount: 0,
                        discount_type: 'rs',
                    },
                ];
                newItems[newItems.length - 1] = inTabCurrency(tab, newItems[newItems.length - 1]);
                this._setTab(tabId, { ...tab, items: newItems });
                return;
            }

            // General / company orders have no single-branch reservation surface.
            if (isGeneral) {
                // `!item.damaged_item_id` on purpose: a damaged line happens to
                // carry the same variant id, and matching on that alone refused
                // to add the normal unit at all — so a cart could never hold a
                // damaged and an undamaged unit of the same product.
                if (tab.items.some((item) => isSameCartLine(item, product))) return;
                const newItems = [
                    ...tab.items,
                    {
                        id: product.id,
                        _rowKey: product._rowKey,
                        name: product.name,
                        qty: 1,
                        rate: cartRateFrom(product, Number(product.tax_percentage ?? 0) || 0),
                        // Marks the rate above as already converted, so a draft
                        // restored after a deploy is not converted a second time.
                        rate_excludes_tax: true,
                        // In batch mode, never past what the picked batch holds.
                        maxQty: withBatchCap(
                            batchFieldsFrom(product),
                            Number.isFinite(Number(product.maxQty)) ? Math.max(0, normalizeQuantity(product.maxQty)) : Number.MAX_SAFE_INTEGER,
                        ),
                        _lastQty: 0,
                        tax_rate: product.tax_rate ?? 0,
                        tax_percentage: Number(product.tax_percentage ?? 0) || 0,
                        tax_mode: product.tax_mode ?? 'none',
                        // Decides which header taxes the quote may include.
                        // Unclassified reads as standard-rated, matching
                        // SaleLineSnapshot — restrictive rather than wrong.
                        is_third_schedule: Boolean(product.third_schedule),
                        // Printed retail price. A third-schedule line is taxed on
                        // this and not on what it sold for, so a discount must
                        // not shrink its tax — without it the quote taxes the
                        // discounted value and checkout rejects the basket.
                        mrp: Number(product.mrp ?? 0) > 0 ? Number(product.mrp) : null,
                        supply_type: product.supply_type ?? 'standard',
                        fed_excisable: Boolean(product.fed_excisable),
                        unit_abbr: product.unit_abbr || '',
                        ...lineDetailFieldsFrom(product),
                        allows_fractional: Boolean(product.allows_fractional),
                        // Service lines carry no stock surface at all and may be
                        // attributed to a worker; goods lines never are.
                        is_service: Boolean(product.is_service),
                        service_pricing_scope: product.service_pricing_scope ?? null,
                        worker_id: null,
                        worker_rate: null,
                        worker_base_override: null,
                        worker_percentage: null,
                        // The catalogue figure is kept as the baseline the
                        // customer's standing rate is weighed against; validateQty
                        // below decides which of the two the line actually gets.
                        base_discount: lineDiscount,
                        base_discount_type: lineDiscountType,
                        discount_source: 'catalogue',
                        discount: lineDiscount,
                        discount_type: lineDiscountType,
                        ...batchFieldsFrom(product),
                    },
                ];
                newItems[newItems.length - 1] = inTabCurrency(tab, newItems[newItems.length - 1]);
                this._setTab(tabId, { ...tab, items: newItems });
                await this.validateQty(newItems.length - 1, tabId);
                return;
            }

            if (branchId === null || branchId === undefined || branchId === '') return;
            const key = `${branchId}-${product.id}`;
            // A service has no branch_stocks row anywhere — nothing to read and
            // nothing to reserve, so availability never gates its line.
            const isServiceLine = Boolean(product.is_service);
            const record = isServiceLine ? null : await offlineDB.branch_products.get(key);
            const currentTab = this.tabs[tabId];
            if (!currentTab || String(currentTab.form?.branch_id) !== String(branchId)) return;
            // As above: a damaged line of this variant is a different line, and
            // so is another batch of it.
            if (currentTab.items.some((item) => isSameCartLine(item, product))) return;

            const real = Number(record?.real_qty ?? 0);
            const reserved = Number(record?.reserved_qty ?? 0);
            const available = isServiceLine ? Number.MAX_SAFE_INTEGER : Math.max(real - reserved, 0);

            // Prefer product search payload; fall back to offline branch cache.
            const cachedType = record?.discount_type === 'percent' ? 'percent' : record?.discount_type === 'rs' ? 'rs' : null;
            const resolvedType =
                product.discount_type === 'percent' || product.discount_type === 'rs'
                    ? product.discount_type === 'percent'
                        ? 'percent'
                        : 'rs'
                    : cachedType || 'rs';
            const resolvedDiscount = Number.isFinite(Number(product.discount))
                ? Number(product.discount) || 0
                : Number(record?.discount_value ?? 0) || 0;
            const resolvedTaxPct = Number.isFinite(Number(product.tax_percentage))
                ? Number(product.tax_percentage) || 0
                : Number(record?.tax_percentage ?? 0) || 0;

            const newItems = [
                ...currentTab.items,
                {
                    id: product.id,
                    _rowKey: product._rowKey,
                    name: product.name,
                    qty: 1,
                    rate: cartRateFrom(product, resolvedTaxPct),
                    // As above: already ex-tax, so the restore migration skips it.
                    rate_excludes_tax: true,
                    // In batch mode, never past what the picked batch holds.
                    maxQty: isServiceLine ? available : withBatchCap(batchFieldsFrom(product), available),
                    _lastQty: 0,
                    tax_rate: product.tax_rate ?? 0,
                    tax_percentage: resolvedTaxPct,
                    tax_mode: product.tax_mode ?? 'none',
                    // Same search-payload-then-branch-cache order as unit_abbr
                    // below; a cache predating the fields reads as standard.
                    is_third_schedule: Boolean(product.third_schedule ?? record?.third_schedule),
                    // The basis that flag puts the tax on, same search-payload-
                    // then-branch-cache order. Null falls back to the line's own
                    // value, which is what the server does with a missing MRP.
                    mrp: Number(product.mrp ?? record?.mrp ?? 0) > 0 ? Number(product.mrp ?? record?.mrp) : null,
                    supply_type: product.supply_type ?? record?.supply_type ?? 'standard',
                    fed_excisable: Boolean(product.fed_excisable ?? record?.fed_excisable),
                    unit_abbr: product.unit_abbr || record?.unit_abbr || '',
                    ...lineDetailFieldsFrom(product, record),
                    // Same search-payload-then-branch-cache order as unit_abbr:
                    // a catalogue row cached before the field existed falls back
                    // to the branch row, which is refreshed on a 30s TTL.
                    allows_fractional: Boolean(product.allows_fractional ?? record?.allows_fractional),
                    base_discount: resolvedDiscount,
                    base_discount_type: resolvedType,
                    discount_source: 'catalogue',
                    discount: resolvedDiscount,
                    discount_type: resolvedType,
                    // Same service fields as the general-order path above.
                    is_service: isServiceLine,
                    service_pricing_scope: product.service_pricing_scope ?? null,
                    worker_id: null,
                    worker_rate: null,
                    worker_base_override: null,
                    worker_percentage: null,
                    ...batchFieldsFrom(product),
                },
            ];
            newItems[newItems.length - 1] = inTabCurrency(currentTab, newItems[newItems.length - 1]);

            this._setTab(tabId, { ...currentTab, items: newItems });
            await this.validateQty(newItems.length - 1, tabId);
        },

        /**
         * Put a tab's sale in another currency (null = base) at a rate (base
         * per unit). Every line is converted from the old currency to the new,
         * so the basket keeps its value; an open payment is dropped, because
         * the total it was opened on no longer exists.
         */
        setTabCurrency(tabId, currencyCode, exchangeRate) {
            const tab = this.tabs[tabId];
            if (!tab) return;
            const code = currencyCode || null;
            const nextRate = code ? String(exchangeRate || '1') : '1';
            const fromRate = tabRate(tab);
            const items = (tab.items || []).map((item) => convertLineMoney(item, fromRate, nextRate));
            this._setTab(tabId, {
                ...tab,
                items,
                form: { ...tab.form, currency_code: code, exchange_rate: code ? nextRate : '' },
                pendingSalePayload: null,
            });
        },

        /**
         * @param {{ batchPool?: Array<number|string>|null }} [options]
         *   batchPool: the search pool of the till this edit came from. In batch
         *   mode a quantity past the line's batch then continues onto the next
         *   batches as their own lines (continueOntoNextBatches); without it
         *   the line only stops at its batch.
         */
        async validateQty(index, tabId = this.activeTabId, { batchPool = null } = {}) {
            const tab = this.tabs[tabId];
            if (!tab) return;

            const item = tab.items[index];
            if (!item) return;

            const branchId = tab.form.branch_id;
            let qty = normalizeQuantity(item.qty);
            // Batch mode: what the cashier asked for past the line's batch.
            let batchOverflow = 0;
            const maxQty = Number(item.maxQty);
            // Clamp to known availability for both single-branch and company-wide
            // carts. Company carts still skip local Dexie reservation below.
            //
            // Never a service. A service holds no stock anywhere, so every
            // availability figure reports zero for one, and honouring that
            // pinned the line at zero for good — each later edit was clamped
            // straight back down and the cashier could not raise it again. The
            // two other places that reconcile a cart already skip services for
            // this reason; this is the funnel every quantity edit goes through,
            // so it heals a draft that already carries a zero ceiling.
            //
            // The ceiling itself follows the unit: hundredths for a continuous
            // one, because branch_stocks is decimal(12,2) and flooring sold 2 kg
            // out of 2.5 and read half a litre as none at all; whole units for a
            // discrete one, which is the only thing it can be sold in.
            if (!item.is_service && Number.isFinite(maxQty) && maxQty >= 0) {
                const ceiling = normalizeQuantity(maxQty);
                const requested = qty;
                qty = Math.min(qty, item.allows_fractional ? ceiling : Math.floor(ceiling));

                // Batch mode: the line stops at its batch — the server sells a
                // line from its own batch alone. Only when the batch, not the
                // shelf, is the limit is there anything left to continue with.
                if (requested > qty && Number.isFinite(Number(item.batch_qty)) && Number(item.batch_qty) <= ceiling) {
                    batchOverflow = normalizeQuantity(requested - qty);
                }
            }

            // Only the edited line is re-priced: whether the customer's standing
            // percentage beats the line's own rupee discount depends on the
            // quantity, and no other line's quantity moved.
            const customerPercent = customerDiscountPercent(tab);
            const items = tab.items.map((candidate, itemIndex) =>
                itemIndex === index ? withAutoLineDiscount({ ...candidate, qty }, customerPercent) : candidate,
            );
            this._setTab(tabId, { ...tab, items });
            reservationGeneration++;
            // General orders skip local single-branch reservation reconciliation.
            if (!isGeneralOrderTab(tab) && !isGeneralOrderBranch(branchId)) {
                await this._refreshAllTabsMaxQty(branchId);
            }

            if (batchOverflow <= 0) return;
            if (batchPool) {
                await this.continueOntoNextBatches(tabId, index, batchOverflow, batchPool);
                return;
            }
            useToast().displayToast({
                message: item.receiving_item_id
                    ? `Batch ${item.batch_number} has only ${qty} left. Search the product again and add another batch for the rest.`
                    : `Only ${qty} of this product is outside a batch. Search the product again and add a batch for the rest.`,
                variant: 'warning',
                duration: 8000,
            });
        },

        /**
         * Batch mode: carry what a line asked for past its batch onto the next
         * batches, earliest valid expiry first, each as its own line at its own
         * batch number, price and quantity left, placed right after the line
         * that ran out. Expired batches are never used.
         *
         * The batches are the search rows themselves (getPosCatalogRowsByIds
         * then posBatchRows), and each line is added through addItemToSale and
         * capped by validateQty, so price, discount, tax, reservations and the
         * per-batch ceiling are exactly what a manual pick of that batch gets.
         * A batch already on the cart takes the units on its existing line
         * rather than a second one. Each line still sells from its own batch
         * alone at checkout, so the server deducts every batch separately.
         *
         * Whatever the batches cannot cover is reported, never invented.
         */
        async continueOntoNextBatches(tabId, sourceIndex, overflow, branchIds) {
            const tab = this.tabs[tabId];
            const source = tab?.items?.[sourceIndex];
            // addItemToSale works on the active tab; an edit on another one
            // stops at its batch, as it would with no pool.
            if (!source || tabId !== this.activeTabId) return;

            const [productRow] = await getPosCatalogRowsByIds({
                variantIds: [source.id],
                branchIds,
                originBranchId: Number(tab.form?.branch_id) || null,
            });
            // The other unexpired batches, then the stock outside every batch
            // (posBatchRows lists it after them) unless that is what ran out.
            const nextBatches = productRow
                ? posBatchRows(productRow).filter((row) =>
                      row.batch_unbatched
                          ? Boolean(source.receiving_item_id)
                          : row.receiving_item_id && !row.batch_expired && String(row.receiving_item_id) !== String(source.receiving_item_id ?? ''),
                  )
                : [];

            let left = normalizeQuantity(overflow);
            let insertAt = sourceIndex + 1;
            for (const row of nextBatches) {
                if (left <= 0) break;
                if (!this.tabs[tabId]) return;

                const existingIndex = this.tabs[tabId].items.findIndex((item) => isSameCartLine(item, row));
                if (existingIndex >= 0) {
                    const existing = this.tabs[tabId].items[existingIndex];
                    const before = normalizeQuantity(existing.qty);
                    // Never past that line's own batch, so validateQty has no
                    // overflow of its own to report mid-continuation.
                    this._setLineQty(tabId, existingIndex, withBatchCap(existing, normalizeQuantity(before + left)));
                    await this.validateQty(existingIndex, tabId);
                    const after = normalizeQuantity(this.tabs[tabId]?.items?.[existingIndex]?.qty);
                    left = normalizeQuantity(Math.max(left - Math.max(after - before, 0), 0));
                    continue;
                }

                await this.addItemToSale(row);
                const items = this.tabs[tabId]?.items ?? [];
                let newIndex = items.length - 1;
                // Refused (a duplicate, or the tab moved on while adding).
                if (newIndex < 0 || !isSameCartLine(items[newIndex], row)) continue;

                // On the line after the one that ran out, not at the bottom.
                if (newIndex !== insertAt && insertAt < items.length) {
                    const reordered = [...items];
                    const [line] = reordered.splice(newIndex, 1);
                    reordered.splice(insertAt, 0, line);
                    this._setTab(tabId, { ...this.tabs[tabId], items: reordered });
                    newIndex = insertAt;
                }

                this._setLineQty(tabId, newIndex, withBatchCap(this.tabs[tabId].items[newIndex], left));
                await this.validateQty(newIndex, tabId);
                const taken = normalizeQuantity(this.tabs[tabId]?.items?.[newIndex]?.qty);
                if (taken <= 0) {
                    // Nothing left on the shelf for it: no empty line behind.
                    await this.removeItem(newIndex, tabId);
                    continue;
                }
                left = normalizeQuantity(Math.max(left - taken, 0));
                insertAt = newIndex + 1;
            }

            if (left > 0) {
                useToast().displayToast({
                    message: `Not enough unexpired batch stock: ${source.name} is short by ${left}.`,
                    variant: 'warning',
                    duration: 8000,
                });
            }
        },

        /** Set one cart line's quantity ahead of validateQty, which clamps it. */
        /**
         * Batch mode: put right the lines a checkout was refused for, where the
         * fix is not the cashier's to choose.
         *
         * - `batch_short`: the picked batch holds less than its line asked for.
         * - `batch_required`: a line with no batch, more than the stock outside
         *   every batch could cover.
         * - `batch_expired` / `batch_missing`: the picked batch has expired, or
         *   its receiving was deleted, since the line was added.
         *
         * Each line keeps what its own source still holds (none, for an expired
         * or vanished batch) and the rest carries onto the next batches as their
         * own lines, at their own prices — exactly as a raised quantity carries
         * over (continueOntoNextBatches); a line left with nothing goes. Anything
         * else, such as a damaged line spread over batches, stays as it was under
         * the server's own sentence.
         *
         * @returns {Promise<boolean>} whether any line was changed
         */
        async repairBatchRefusals(tabId, shortfall, batchPool) {
            if (!this.tabs[tabId] || !Array.isArray(shortfall) || !Array.isArray(batchPool) || !batchPool.length) return false;
            const reasons = new Set(['batch_short', 'batch_required', 'batch_expired', 'batch_missing']);
            // Highest line first, so dropping one never moves a line still to do;
            // a continuation only ever inserts after the line it continues.
            const rows = shortfall
                .filter((row) => reasons.has(row?.reason) && Number.isInteger(Number(row?.sale_item_index)) && Number(row.sale_item_index) >= 0)
                .sort((a, b) => Number(b.sale_item_index) - Number(a.sale_item_index));

            let repaired = false;
            const done = new Set();
            for (const row of rows) {
                const index = Number(row.sale_item_index);
                if (done.has(index)) continue;
                done.add(index);

                const item = this.tabs[tabId]?.items?.[index];
                if (!item || item.is_service || item.damaged_item_id != null) continue;
                const picked = Boolean(item.receiving_item_id);
                if ((row.reason === 'batch_required') === picked) continue;

                const qty = normalizeQuantity(item.qty);
                const gone = row.reason === 'batch_expired' || row.reason === 'batch_missing';
                const holds = gone ? 0 : Math.max(normalizeQuantity(row.available ?? 0), 0);
                const keep = Math.min(qty, holds);
                const overflow = normalizeQuantity(qty - keep);
                if (overflow <= 0) continue;

                this._setTab(tabId, {
                    ...this.tabs[tabId],
                    items: this.tabs[tabId].items.map((line, lineIndex) =>
                        lineIndex === index
                            ? {
                                  ...line,
                                  qty: keep,
                                  batch_qty: holds,
                                  maxQty: Number.isFinite(Number(line.maxQty)) ? Math.min(Number(line.maxQty), holds) : holds,
                              }
                            : line,
                    ),
                });
                await this.continueOntoNextBatches(tabId, index, overflow, batchPool);
                if (keep <= 0) await this.removeItem(index, tabId);
                repaired = true;
            }

            return repaired;
        },

        _setLineQty(tabId, index, qty) {
            const tab = this.tabs[tabId];
            if (!tab?.items?.[index]) return;
            this._setTab(tabId, {
                ...tab,
                items: tab.items.map((item, itemIndex) => (itemIndex === index ? { ...item, qty } : item)),
            });
        },

        async resetReservations() {
            const branchId = this.tabs[this.activeTabId]?.form?.branch_id;
            await resetAllReservations(branchId);
            reservationGeneration++;
            const branchIds =
                branchId !== null && branchId !== undefined && branchId !== ''
                    ? [branchId]
                    : [
                          ...new Set(
                              Object.values(this.tabs)
                                  .map((tab) => tab?.form?.branch_id)
                                  .filter((id) => id !== null && id !== undefined && id !== ''),
                          ),
                      ];
            for (const id of branchIds) {
                await this._refreshAllTabsMaxQty(id);
            }
        },

        async removeItem(index, tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab) return;

            const newItems = [...tab.items];
            newItems.splice(index, 1);
            this._setTab(tabId, { ...tab, items: newItems });
            reservationGeneration++;
            const branchId = tab.form?.branch_id;
            if (!isGeneralOrderBranch(branchId)) {
                await this._refreshAllTabsMaxQty(branchId);
            }
        },

        openDeleteModal(index) {
            const tab = this.tabs[this.activeTabId];
            if (!tab) return;
            this._setTab(this.activeTabId, { ...tab, deleteIndex: index, showDeleteModal: true });
        },

        async confirmDelete() {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            if (tab.deleteIndex !== null) await this.removeItem(tab.deleteIndex, tabId);
            if (!this.tabs[tabId]) return;
            this._setTab(tabId, {
                ...this.tabs[tabId],
                showDeleteModal: false,
                deleteIndex: null,
            });
        },

        closeDeleteModal() {
            const tab = this.tabs[this.activeTabId];
            if (!tab) return;
            this._setTab(this.activeTabId, { ...tab, showDeleteModal: false, deleteIndex: null });
        },

        async _refreshAllTabsMaxQty(branchId) {
            if (branchId === null || branchId === undefined || branchId === '') return;
            const branchKey = String(branchId);
            const sessionId = getPosSessionId();
            // Absent on a cache predating schema v36 and in test doubles: then
            // this tab is the only session there is, which is the old behaviour.
            const reservations = offlineDB.cart_reservations ?? null;

            const reconcile = async () => {
                // A cart may change while IndexedDB is yielding. Repeat until
                // both the records and Pinia snapshot represent one generation.
                for (let attempt = 0; attempt < 5; attempt++) {
                    const generationAtStart = reservationGeneration;
                    let itemPlans = [];
                    let ownRowsChanged = false;

                    const tables = reservations ? [offlineDB.branch_products, reservations] : [offlineDB.branch_products];
                    await offlineDB.transaction('rw', tables, async () => {
                        // Through the `branch_id` index, not a cursor scan.
                        // `.filter()` in Dexie walks and deserializes every row
                        // in the store, so a till reconciling its cart re-read
                        // every branch it had ever cached — on every add, remove
                        // and quantity commit. `anyOf` keeps the tolerance the
                        // predicate had: older schema versions stored branch_id
                        // as a number, newer ones as a string.
                        const records = await offlineDB.branch_products
                            .where('branch_id')
                            .anyOf([branchKey, Number(branchKey)])
                            .toArray();
                        const recordsByKey = new Map(records.map((record) => [String(record.key), record]));
                        const reservedBefore = new Map(records.map((record) => [String(record.key), normalizeQuantity(record.reserved_qty)]));

                        // What the other open tabs of this browser hold here.
                        // Their rows are the truth about their carts; this
                        // tab's own rows are about to be rewritten from its
                        // carts, and a dead session's rows are dropped now
                        // rather than waited out.
                        const peerHeldByKey = new Map();
                        const ownRowsByKey = new Map();
                        if (reservations) {
                            const live = new Set(livePosSessionIds());
                            const stale = [];
                            for (const row of await reservations.where('branch_id').equals(branchKey).toArray()) {
                                const owner = String(row.session_id ?? '');
                                const key = `${branchKey}-${Number(row.variant_id)}`;
                                if (owner === sessionId) {
                                    ownRowsByKey.set(key, row);
                                } else if (live.has(owner)) {
                                    peerHeldByKey.set(key, normalizeQuantity((peerHeldByKey.get(key) || 0) + normalizeQuantity(row.qty)));
                                } else {
                                    stale.push(row.key);
                                }
                            }
                            if (stale.length) await reservations.bulkDelete(stale);
                        }

                        const entriesByKey = new Map();
                        const variantByKey = new Map();
                        for (const [tabId, tab] of Object.entries(this.tabs)) {
                            if (String(tab?.form?.branch_id ?? '') !== branchKey) continue;
                            for (const [index, item] of (tab.items || []).entries()) {
                                const variantId = Number(item.id);
                                if (!Number.isSafeInteger(variantId) || variantId <= 0) continue;
                                // A service holds no stock: there is no branch
                                // stock row to reserve against, so a cart
                                // reconciliation must never clamp its quantity to
                                // zero. Skip it here; it is absent from the
                                // reservation plan and passes through untouched.
                                if (item.is_service) continue;
                                const key = `${branchKey}-${variantId}`;
                                const entries = entriesByKey.get(key) || [];
                                entries.push({
                                    tabId,
                                    index,
                                    id: item.id,
                                    desiredQty: normalizeQuantity(item.qty),
                                    // A damaged line's sale cap is its report
                                    // quantity, not the branch stock level.
                                    damageQty: Number(item.damage_qty),
                                    // A batch line's is its batch, likewise.
                                    batchQty: Number(item.batch_qty),
                                    // The one source a batch-mode line draws:
                                    // its batch, or the stock outside every
                                    // batch. Two lines of one source share it.
                                    batchSource: item.receiving_item_id
                                        ? `b${item.receiving_item_id}`
                                        : Number.isFinite(Number(item.batch_qty))
                                          ? `u${variantId}`
                                          : null,
                                });
                                entriesByKey.set(key, entries);
                                variantByKey.set(key, variantId);
                            }
                        }

                        itemPlans = [];
                        const ownHeldByKey = new Map();
                        for (const [key, entries] of entriesByKey) {
                            const record = recordsByKey.get(key);
                            const realQty = normalizeQuantity(record?.real_qty);
                            const peerHeld = peerHeldByKey.get(key) || 0;
                            // Peers hold first: a line here only takes what they
                            // left, the same way a later tab of this store only
                            // takes what the earlier ones left.
                            let remainingQty = Math.max(realQty - peerHeld, 0);
                            let ownHeld = 0;

                            // Batch mode: what each source holds, drawn down as
                            // lines claim it — two tabs selling one batch share
                            // it, not each get the whole of it. A line's own
                            // `batch_qty` is what its row said when it was
                            // added, so the lowest of them is the cap.
                            const sourceCap = new Map();
                            for (const entry of entries) {
                                if (!entry.batchSource || !Number.isFinite(entry.batchQty)) continue;
                                const cap = sourceCap.get(entry.batchSource);
                                sourceCap.set(entry.batchSource, cap === undefined ? entry.batchQty : Math.min(cap, entry.batchQty));
                            }
                            const sourceHeld = new Map();

                            for (const entry of entries) {
                                const sourceLeft = entry.batchSource && sourceCap.has(entry.batchSource)
                                    ? Math.max(sourceCap.get(entry.batchSource) - (sourceHeld.get(entry.batchSource) || 0), 0)
                                    : Number.MAX_SAFE_INTEGER;
                                entry.assignedQty = normalizeQuantity(Math.min(entry.desiredQty, remainingQty, sourceLeft));
                                remainingQty -= entry.assignedQty;
                                ownHeld += entry.assignedQty;
                                if (entry.batchSource) {
                                    sourceHeld.set(entry.batchSource, normalizeQuantity((sourceHeld.get(entry.batchSource) || 0) + entry.assignedQty));
                                }
                            }

                            for (const entry of entries) {
                                let ceiling = Math.max(realQty - peerHeld - (ownHeld - entry.assignedQty), 0);
                                if (Number.isFinite(entry.damageQty)) ceiling = Math.min(ceiling, entry.damageQty);
                                if (entry.batchSource && sourceCap.has(entry.batchSource)) {
                                    const heldByOthers = (sourceHeld.get(entry.batchSource) || 0) - entry.assignedQty;
                                    ceiling = Math.min(ceiling, Math.max(sourceCap.get(entry.batchSource) - heldByOthers, 0));
                                }
                                itemPlans.push({ ...entry, maxQty: normalizeQuantity(ceiling) });
                            }

                            ownHeldByKey.set(key, normalizeQuantity(ownHeld));
                            if (record) {
                                record.reserved_qty = normalizeQuantity(peerHeld + ownHeld);
                            }
                        }

                        // Rows no cart of this tab represents keep only what the
                        // peers hold, so a tab or cart deletion never leaves a
                        // stale reservation behind.
                        for (const record of records) {
                            const key = String(record.key);
                            if (!entriesByKey.has(key)) {
                                record.reserved_qty = peerHeldByKey.get(key) || 0;
                            }
                        }

                        const changed = records.filter((record) => normalizeQuantity(record.reserved_qty) !== reservedBefore.get(String(record.key)));
                        if (changed.length) await offlineDB.branch_products.bulkPut(changed);

                        if (reservations) {
                            const now = new Date().toISOString();
                            const puts = [];
                            const deletes = [];
                            for (const [key, qty] of ownHeldByKey) {
                                const existing = ownRowsByKey.get(key);
                                ownRowsByKey.delete(key);
                                if (qty <= 0) {
                                    if (existing) deletes.push(existing.key);
                                    continue;
                                }
                                if (existing && normalizeQuantity(existing.qty) === qty) continue;
                                puts.push({
                                    key: `${sessionId}|${key}`,
                                    session_id: sessionId,
                                    branch_id: branchKey,
                                    variant_id: variantByKey.get(key),
                                    qty,
                                    updated_at: now,
                                });
                            }
                            // Held on an earlier pass, in no cart now.
                            for (const row of ownRowsByKey.values()) deletes.push(row.key);
                            if (puts.length) await reservations.bulkPut(puts);
                            if (deletes.length) await reservations.bulkDelete(deletes);
                            ownRowsChanged = puts.length > 0 || deletes.length > 0;
                        }
                    });

                    const stateChanged =
                        generationAtStart !== reservationGeneration ||
                        itemPlans.some((plan) => {
                            const current = this.tabs[plan.tabId]?.items?.[plan.index];
                            return !current || String(current.id) !== String(plan.id) || normalizeQuantity(current.qty) !== plan.desiredQty;
                        });

                    if (stateChanged) continue;

                    const plansByTab = new Map();
                    for (const plan of itemPlans) {
                        const plans = plansByTab.get(plan.tabId) || new Map();
                        plans.set(plan.index, plan);
                        plansByTab.set(plan.tabId, plans);
                    }

                    for (const [tabId, plans] of plansByTab) {
                        const tab = this.tabs[tabId];
                        if (!tab || String(tab.form?.branch_id ?? '') !== branchKey) continue;
                        this.tabs[tabId] = {
                            ...tab,
                            items: tab.items.map((item, index) => {
                                const plan = plans.get(index);
                                return plan
                                    ? {
                                          ...item,
                                          qty: plan.assignedQty,
                                          _lastQty: plan.assignedQty,
                                          maxQty: plan.maxQty,
                                      }
                                    : item;
                            }),
                        };
                    }

                    await this.flushPersistToIDB();
                    // Only when this tab's own holds moved. A peer answering
                    // this by re-deriving its caps and finding nothing to
                    // change says nothing back, so two tabs cannot ping-pong.
                    if (ownRowsChanged) publishPosReservationChange({ branchId: branchKey });
                    return;
                }

                throw new Error(`Could not reconcile branch ${branchKey} reservations because carts kept changing.`);
            };

            const run = reservationChain.then(reconcile, reconcile);
            reservationChain = run.catch((err) => {
                console.error('Reservation reconciliation failed:', err);
            });
            return run;
        },

        async prepareSale() {
            const tabId = this.activeTabId;
            let tab = this.tabs[tabId];
            if (!tab) {
                return;
            }
            if (tab.isSubmitting) {
                return;
            }

            const branchId = tab.form.branch_id;
            const isGeneral = isGeneralOrderTab(tab);

            if (!tab.items.length) {
                useToast().displayToast({ message: 'Please add at least one product', variant: 'error' });
                return;
            }
            if (isGeneralOrderBranch(branchId)) {
                useToast().displayToast({
                    message: 'Please select the front-office branch for this order',
                    variant: 'error',
                });
                return;
            }
            if (isGeneral && !canMutateOnline()) {
                useToast().displayToast({
                    message: 'Company-wide stock search requires an internet connection.',
                    variant: 'warning',
                });
                return;
            }

            reservationGeneration++;
            // Services are left out of the question, not just out of the answer:
            // nothing stocks one, so the endpoint can only report zero for it,
            // and an all-service basket has nothing to ask about at all.
            const stockedVariantIds = (tab.items ?? []).filter((item) => !item.is_service).map((item) => Number(item.id));
            if (!isGeneral) {
                await this._refreshAllTabsMaxQty(branchId);
            } else if (isMultiBranchAddonEnabled() && canMutateOnline() && stockedVariantIds.length) {
                try {
                    const availRes = await apiClient.post('sale/availability', {
                        origin_branch_id: branchId,
                        allocation_mode: 'company',
                        product_variant_ids: stockedVariantIds,
                    });
                    await this.applyServerAvailability(availRes.data?.data ?? availRes.data ?? {});
                } catch (availErr) {
                    console.warn('Aggregate availability refresh before payment failed.', availErr);
                }
            }
            tab = this.tabs[tabId];
            if (!tab || this.activeTabId !== tabId) return;

            // Whole quantities for discrete units; a continuous unit (kg, hour,
            // kilometre) may go to two decimals, matching QuantityMath
            // server-side. Everything else about the line is unchanged.
            // Compared with a tolerance: 1.1 * 100 is 110.00000000000001 in a
            // double, so an exact test refused 1.1 kg as "more than two decimals".
            const invalid = tab.items.some((item) => {
                const qty = Number(item.qty);
                const qtyOk = Boolean(item.allows_fractional)
                    ? Number.isFinite(qty) && qty > 0 && Math.abs(qty * 100 - Math.round(qty * 100)) < 1e-6
                    : Number.isSafeInteger(qty) && qty > 0;
                return !qtyOk || !Number.isFinite(Number(item.rate)) || Number(item.rate) < 0;
            });
            if (invalid) {
                useToast().displayToast({ message: 'Items must have a valid quantity and price', variant: 'error' });
                return;
            }

            const items = JSON.parse(JSON.stringify(tab.items));
            const summary = calculateSaleSummary(items, pricedForm(tab.form));
            const draftFingerprint = saleDraftFingerprint({ ...tab, items });
            const existingPending = tab.pendingSalePayload;
            const clientRequestId =
                existingPending?.draftFingerprint === draftFingerprint && existingPending?.clientRequestId
                    ? existingPending.clientRequestId
                    : createSaleRequestId();

            let previewAllocations = null;
            if (isMultiBranchAddonEnabled() && canMutateOnline()) {
                try {
                    const previewRes = await apiClient.post('sale/allocation-preview', {
                        origin_branch_id: branchId,
                        allocation_mode: isGeneral ? 'company' : undefined,
                        items: items.map((i) => ({
                            product_variant_id: i.id,
                            quantity: i.qty,
                        })),
                    });
                    previewAllocations = previewRes.data?.data ?? null;
                    if (previewAllocations && previewAllocations.allocatable === false) {
                        const short = Array.isArray(previewAllocations.shortfall) ? previewAllocations.shortfall : [];
                        const detail = short
                            .slice(0, 3)
                            .map((s) => `#${s.product_variant_id}: need ${s.requested}, have ${s.available}`)
                            .join('; ');
                        useToast().displayToast({
                            message: detail ? `Insufficient stock across branches — ${detail}` : 'Insufficient stock across branches for this cart.',
                            variant: 'error',
                        });
                        return;
                    }
                } catch (error) {
                    const status = error?.response?.status;
                    useToast().displayToast({
                        message:
                            error.response?.data?.message ||
                            (status === 422
                                ? 'Fulfillment pool is misconfigured for this order.'
                                : 'Could not verify stock allocation. Check your connection and try again.'),
                        variant: 'error',
                    });
                    return;
                }
            }

            this._setTab(tabId, {
                ...tab,
                pendingSalePayload: {
                    items,
                    // Always persist initiation / front-office branch on the sale.
                    // The cash till is derived from it at submit time.
                    branchId,
                    companyStockSearch: isGeneral,
                    previewAllocations,
                    clientRequestId,
                    draftFingerprint,
                    // Delivery charges are collected later, inside PaymentModal,
                    // so this summary necessarily excludes them. submitSale()
                    // recomputes from this snapshot once delivery is known
                    // rather than adding it on afterwards — the server folds
                    // delivery into the same total, and anything derived from
                    // it (advance tax) must see the same base.
                    formSnapshot: { ...tab.form },
                    ...summary,
                },
            });

            this.paymentTabId = tabId;
            this.paymentModal = true;
        },

        setLivePayment(data) {
            this.livePayment = { ...this.livePayment, ...data };
        },
        setLiveDelivery(data) {
            this.liveDelivery = { ...this.liveDelivery, ...data };
        },
        setMultiSaleOrder(tabId, order) {
            const next = { ...this.multiSaleOrders };
            if (order) next[tabId] = order;
            else delete next[tabId];
            this.multiSaleOrders = next;
        },
        resetLivePayment() {
            // Every key setLivePayment can write is cleared here. A key left out
            // survives the merge into the next sale, so the customer display
            // would still be showing the last buyer's account or change.
            this.livePayment = {
                method: 'cash',
                amount: '',
                transaction_id: '',
                selectedAccount: null,
                activeModalTab: 'payment',
                creditChangeToWallet: false,
                walletCreditAmount: 0,
                tenders: [],
                splitPaid: 0,
                splitRemaining: 0,
                splitChange: 0,
            };
            this.liveDelivery = { name: '', phone: '', address: '' };
        },
        setAvailableBranches(branches) {
            this.availableBranches = branches;
        },
        /**
         * Merge server availability (409 STOCK_CONFLICT or /sale/availability)
         * into Dexie. Display / maxQty only — never used to authorise a sale.
         */
        async applyServerAvailability(payload = {}) {
            const availability = Array.isArray(payload.availability)
                ? payload.availability
                : Array.isArray(payload.data?.availability)
                  ? payload.data.availability
                  : [];
            if (availability.length === 0) return;

            await cacheVariantAvailability(availability);

            if (!offlineDB.branch_products) return;

            await offlineDB.transaction('rw', offlineDB.branch_products, async () => {
                for (const row of availability) {
                    const variantId = Number(row.product_variant_id ?? row.variant_id);
                    if (!Number.isSafeInteger(variantId) || variantId <= 0) continue;
                    const branches = Array.isArray(row.by_branch) ? row.by_branch : [];
                    for (const branchRow of branches) {
                        const branchId = Number(branchRow.branch_id);
                        // Hundredths, not whole units: these figures come off a
                        // decimal(12,2) column, so flooring them rewrote the
                        // cached shelf level lower than the branch really holds.
                        const available = normalizeQuantity(branchRow.available);
                        if (!Number.isSafeInteger(branchId) || branchId <= 0) continue;
                        const key = `${branchId}-${variantId}`;
                        const record = await offlineDB.branch_products.get(key);
                        if (!record) continue;
                        const reserved = normalizeQuantity(record.reserved_qty);
                        record.real_qty = normalizeQuantity(available + reserved);
                        await offlineDB.branch_products.put(record);
                    }
                }
            });

            const tabId = this.paymentTabId ?? this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab?.items?.length) return;

            const byVariant = new Map(
                availability.map((row) => [
                    Number(row.product_variant_id ?? row.variant_id),
                    normalizeQuantity(row.aggregate_available),
                ]),
            );
            const nextItems = tab.items.map((item) => {
                // A service line is in this payload because it is in the cart,
                // not because anything stocks it: `stockAvailability` answers
                // for every id it is asked about, and there is no branch_stocks
                // row behind a service, so its figure is always zero. Writing
                // that onto the ceiling made the job unsellable.
                if (item.is_service) return item;
                const aggregate = byVariant.get(Number(item.id));
                if (aggregate == null) return item;
                return { ...item, maxQty: withBatchCap(item, aggregate) };
            });
            this._setTab(tabId, { ...tab, items: nextItems });
        },
        /**
         * @param {{ batchPool?: Array<number|string>|null }} [options]
         *   batchPool: the till's search pool, as validateQty takes it. In batch
         *   mode a refusal that names a batch line is then repaired on the cart
         *   (repairBatchRefusals) rather than only reported.
         */
        async submitSale(paymentData, { batchPool = null } = {}) {
            const tabId = this.paymentTabId ?? this.activeTabId;
            let tab = this.tabs[tabId];
            let pending = tab?.pendingSalePayload;

            if (!pending) return;
            if (this.activeTabId !== tabId) {
                useToast().displayToast({
                    message: 'Return to the sale tab that opened this payment before submitting.',
                    variant: 'warning',
                });
                return;
            }

            if (!canMutateOnline()) {
                useToast().displayToast({
                    message: 'You are offline — changes are disabled until the connection returns.',
                    variant: 'warning',
                });
                const err = new Error('This action requires an internet connection.');
                err.code = 'ERR_OFFLINE_WRITE';
                throw err;
            }

            const { items, branchId } = pending;
            const isGeneral = Boolean(pending.companyStockSearch) || isGeneralOrderTab(tab);

            if (isGeneralOrderBranch(branchId)) {
                useToast().displayToast({
                    message: 'Please select the front-office branch for this order',
                    variant: 'error',
                });
                return;
            }

            if (!items?.length) {
                useToast().displayToast({ message: 'No items found', variant: 'error' });
                return;
            }

            const currentDraftFingerprint = saleDraftFingerprint(tab);
            if (pending.draftFingerprint && pending.draftFingerprint !== currentDraftFingerprint) {
                this.paymentModal = false;
                this.paymentTabId = null;
                useToast().displayToast({
                    message: 'The sale changed after payment opened. Review it and open payment again.',
                    variant: 'warning',
                });
                return;
            }

            if (!pending.clientRequestId || !pending.draftFingerprint) {
                pending = {
                    ...pending,
                    clientRequestId: pending.clientRequestId || createSaleRequestId(),
                    draftFingerprint: pending.draftFingerprint || currentDraftFingerprint,
                };
                tab = { ...tab, pendingSalePayload: pending };
                this._setTab(tabId, tab);
                await this.flushPersistToIDB();
            }

            const tenderedAmount = Number(paymentData?.amount);
            if (!Number.isFinite(tenderedAmount) || tenderedAmount < 0) {
                useToast().displayToast({ message: 'Enter a valid non-negative payment amount', variant: 'error' });
                const error = new Error('Invalid payment amount.');
                error.code = 'INVALID_PAYMENT_AMOUNT';
                throw error;
            }

            // A charge on the invoice, at the sale's places like the rest of it.
            const deliveryCharges = roundTo(paymentData?.delivery?.charges ?? 0, saleDecimals());
            // Recompute rather than add delivery to the prepared total: the
            // server runs the whole sale through SaleFinancialCalculator with
            // delivery included, and going through the same function here keeps
            // the quoted total identical to the persisted one. Adding it
            // afterwards round-trips through fromCents/toCents a second time and
            // can drift a paisa, which the server rejects as a tampered total.
            // The buyer's advance-tax standing, as resolved by the server when
            // the customer was attached. Echoed back so the server can reject a
            // quote raised against a rate that has since changed, rather than
            // silently charging a total the cashier never displayed.
            const advanceTax = tab.customer?.advance_tax ?? null;
            const advanceTaxRate = Number(advanceTax?.rate ?? 0) || 0;

            const settlement = calculateSaleSummary(items, {
                ...pricedForm(pending.formSnapshot),
                delivery_charges: deliveryCharges,
                advance_tax_rate: advanceTaxRate,
            });
            const grandTotal = settlement.finalTotal;

            // Multi-currency: the sale settles in its own currency; the tender
            // is in the currency of where it lands (the drawer, a wallet and a
            // card machine hold base, an account its own). Where the two differ
            // the tender converts at its rate and the sale's booking rate, and
            // change is handed back in the tender's currency. Same arithmetic as
            // CreateSaleJob::normalizeCrossCurrencyTender, which re-checks it.
            const saleCurrency = tab.form.currency_code || null;
            const bookingRate = saleCurrency ? String(tab.form.exchange_rate || '1') : '1';
            const tenderCurrency = paymentData?.tender_currency || null;
            const tenderRate = tenderCurrency ? String(paymentData?.tender_rate || '1') : '1';
            const crossCurrency = tenderCurrency !== saleCurrency;
            const tenderCents = Math.round(tenderedAmount * 100);
            const grandTotalCents = Math.round(grandTotal * 100);
            const dueInTender = crossCurrency
                ? documentToTenderCents(grandTotalCents, saleCurrency, bookingRate, tenderCurrency, tenderRate) / 100
                : grandTotal;
            // Split tender (docs/pos-split-tender-plan.md): two or more legs,
            // applied the server's way — wallet, card and account, cash last
            // with the change — through the same arithmetic it re-checks
            // (Currency::checkoutTenders). A one-payment checkout never takes
            // this path and sends exactly what it always has.
            const splitLegs = Array.isArray(paymentData?.tenders) && paymentData.tenders.length > 1 ? paymentData.tenders : null;
            const split = splitLegs
                ? splitTender(
                      splitLegs.map((leg, key) => ({
                          key,
                          method: leg.method,
                          amount: leg.amount,
                          currency: leg.tender_currency || null,
                          rate: leg.tender_rate || '1',
                          order: Number(leg.order) || 0,
                      })),
                      { finalCents: grandTotalCents, documentCurrency: saleCurrency, bookingRate, baseCode: useConfigurationStore().baseCurrency },
                  )
                : null;
            if (split?.error) {
                useToast().displayToast({ message: split.error.message, variant: 'error' });
                const error = new Error(split.error.message);
                error.code = 'INVALID_PAYMENT_AMOUNT';
                throw error;
            }

            const settledAmount = split
                ? roundMoney(split.paidCents / 100)
                : crossCurrency
                  ? tenderCents >= Math.round(dueInTender * 100)
                      ? grandTotal
                      : roundMoney(Math.min(grandTotal, appliedToDocumentCents(tenderCents, tenderCurrency, tenderRate, saleCurrency, bookingRate) / 100))
                  : roundMoney(Math.min(tenderedAmount, grandTotal));
            const paymentStatus = grandTotal > 0 && settledAmount >= grandTotal ? 'paid' : settledAmount > 0 ? 'partially_paid' : 'unpaid';

            const isCash = split ? splitLegs.some((leg) => leg.method === 'cash') : paymentData?.method === 'cash';
            const cashExcess = split ? roundMoney(split.cashChangeCents / 100) : isCash ? roundMoney(Math.max(tenderedAmount - dueInTender, 0)) : 0;
            const mayCreditChange = isCash && Boolean(paymentData?.credit_change_to_wallet) && Boolean(tab.customer?.id);
            const walletChange = mayCreditChange ? roundMoney(Math.min(normalizeNonNegativeNumber(paymentData?.change_amount), cashExcess)) : 0;
            const cashChange = isCash ? roundMoney(Math.max(cashExcess - walletChange, 0)) : 0;

            this._setTab(tabId, { ...this.tabs[tabId], isSubmitting: true });
            await this.flushPersistToIDB();

            // Cash taken at an open till lands in that till's branch, whatever
            // branch raised the order; the server refuses a mismatch. Without
            // a session the cash till follows the order-from branch as before.
            const tillBranchId = isCash ? useTillStore().cashRule(branchId).paymentBranchId : null;

            const payload = {
                client_request_id: pending.clientRequestId,
                // Front-office / initiation branch — always stored on the sale.
                branch_id: branchId,
                payment_branch_id: tillBranchId ?? branchId,
                customer_id: tab.customer.id ?? null,
                date: tab.form.date,
                note: tab.form.notes ?? null,

                // Every derived total comes from `settlement`, the one summary
                // that knows the delivery charge. Mixing in the pre-delivery
                // `pending` figures is what let the two disagree.
                main_discount_type: tab.form.discount_type,
                main_discount_value: settlement.discountValue,
                sales_tax_percentage: settlement.salesTaxPercentage,

                gross_amount: settlement.grossAmount,
                total_discount_amount: settlement.discountAmount,
                taxable_amount: settlement.taxableAmount,
                tax_amount: settlement.taxAmount,
                net_amount: settlement.netAmount,
                final_total: grandTotal,
                // The places the cart was priced at; CreateSaleJob refuses a
                // cart priced at other places than its own setting.
                decimal_places: saleDecimals(),
                // And whether its total was rounded to the rupee, held to the
                // setting the same way.
                round_total: saleRoundTotal() ? 1 : 0,
                fed_tax: settlement.fedTaxPercentage,
                extra_tax: settlement.extraTaxPercentage,
                fed_tax_amount: settlement.fedTaxAmount,
                extra_tax_amount: settlement.extraTaxAmount,

                advance_tax_section: advanceTax?.section ?? null,
                advance_tax_rate: advanceTaxRate,
                advance_tax_base: settlement.advanceTaxBase,
                advance_tax_amount: settlement.advanceTaxAmount,

                status: 'pending',
                payment_status: paymentStatus,
                paid_amount: settledAmount,
                auto_tax_rate: paymentData.autoTaxRate || 0,
                auto_tax_amount: paymentData.autoTaxAmount || 0,
                payment_method: paymentData.method,
                transaction_id: paymentData.transaction_id || null,
                // Null on a card machine: the server resolves the machine for
                // the payment branch itself and the money waits at the
                // acquirer, not in any account. PaymentModal sends null once
                // the resolution names one; otherwise the account as before.
                account_id: paymentData.account_id || null,
                wallet_payment: paymentData.method === 'wallet',
                // Present only when the cashier accepted the over-limit dialog.
                credit_limit_override: paymentData.credit_limit_override === true,
                credit_change_to_wallet: mayCreditChange && walletChange > 0,
                change_amount: walletChange,
                cash_received_amount: isCash ? tenderedAmount : null,
                cash_change_amount: cashChange,

                // The sale's currency and booking rate, and — where the tender
                // is in another currency — what changed hands and at what rate.
                ...(saleCurrency ? { currency_code: saleCurrency, exchange_rate: bookingRate } : {}),
                ...(crossCurrency || tenderCurrency
                    ? { tender_amount: tenderedAmount, ...(tenderCurrency ? { tender_rate: tenderRate } : {}) }
                    : {}),

                delivery_charges: deliveryCharges,

                ...(isMultiBranchAddonEnabled() && isGeneral ? { allocation_mode: 'company' } : {}),

                // Same rule as automotive_id below: absent means "no opinion",
                // which defers to the customer's preference and then the
                // site-wide rule. PaymentModal only puts the key in the payload
                // when the cashier deliberately overrode this one sale.
                ...(typeof paymentData.post_sale_reminder === 'boolean' ? { post_sale_reminder: paymentData.post_sale_reminder } : {}),

                // Never send automotive_id: null — absent means "no automotive",
                // and the server treats a present-but-null value as an attempt to
                // link. The job card only rides along when the cashier actually
                // filled something in, so a parts-counter sale stays untouched.
                ...(tab.form.automotive_id
                    ? {
                          automotive_id: Number(tab.form.automotive_id),
                          ...(hasServiceRecordInput(tab.form.service_record)
                              ? { service_record: compactServiceRecord(tab.form.service_record) }
                              : {}),
                      }
                    : {}),

                delivery_information: paymentData.delivery
                    ? {
                          name: paymentData.delivery.name,
                          phone: paymentData.delivery.phone,
                          address: paymentData.delivery.address,
                          instructions: paymentData.delivery.instructions,
                          date: paymentData.delivery.date,
                          charges: deliveryCharges,
                      }
                    : null,

                driver_information: paymentData.delivery
                    ? {
                          driver_name: paymentData.delivery.driver_name,
                          driver_contact: paymentData.delivery.driver_contact,
                          vehicle_number: paymentData.delivery.vehicle_number,
                          courier_company: paymentData.delivery.courier_company,
                          tracking_number: paymentData.delivery.tracking_number,
                      }
                    : null,

                items: items.map((i) => ({
                    product_variant_id: i.id,
                    quantity: i.qty,
                    amount: i.rate,
                    discount_type: i.discount_type === 'percent' ? 'percent' : 'rs',
                    discount_value: Number(i.discount) || 0,
                    // The server verifies the damage price against the frozen
                    // tax snapshot and marks the unit sold in the same
                    // transaction as the stock allocation.
                    ...(i.damaged_item_id != null ? { damaged_item_id: i.damaged_item_id } : {}),
                    // Batch mode: the batch the cashier picked. The server draws
                    // it first and any excess earliest-expiry-first.
                    ...(i.receiving_item_id ? { receiving_item_id: Number(i.receiving_item_id) } : {}),
                    // Worker attribution rides only when picked. An empty
                    // percentage defers to the worker's standing rate
                    // server-side, so it is sent as null rather than 0.
                    ...(i.worker_id
                        ? {
                              worker_id: Number(i.worker_id),
                              worker_base:
                                  i.worker_base_override === null || i.worker_base_override === undefined || i.worker_base_override === ''
                                      ? null
                                      : Number(i.worker_base_override),
                              worker_rate:
                                  i.worker_rate_override === null || i.worker_rate_override === undefined || i.worker_rate_override === ''
                                      ? null
                                      : Number(i.worker_rate_override),
                              worker_percentage:
                                  i.worker_percentage === null || i.worker_percentage === undefined || i.worker_percentage === ''
                                      ? null
                                      : Number(i.worker_percentage),
                          }
                        : {}),
                    // Company stock search lets the server allocate across the pool
                    // from the initiation branch; single-branch carts pin the line.
                    ...(isGeneral ? {} : { branch_id: branchId }),
                })),
            };

            if (split) {
                // The legs carry the methods; the single-tender fields describe
                // a one-payment checkout and the server refuses them beside the
                // list. Each leg names its account only off a card machine, its
                // reference when it has one, and its rate when it is foreign.
                for (const key of ['payment_method', 'transaction_id', 'account_id', 'wallet_payment', 'tender_amount', 'tender_rate']) {
                    delete payload[key];
                }
                payload.cash_received_amount = null;
                payload.tenders = splitLegs.map((leg) => ({
                    method: leg.method,
                    amount: leg.amount,
                    ...(leg.account_id ? { account_id: Number(leg.account_id) } : {}),
                    ...(leg.transaction_id ? { transaction_id: leg.transaction_id } : {}),
                    ...(leg.tender_currency ? { tender_rate: leg.tender_rate } : {}),
                }));
            }

            try {
                const res = await apiClient.post('sale/createSale', payload);

                if (res.data?.status === 'success' || res.data?.success) {
                    const newSaleId = res.data?.sale_id ?? null;
                    if (!Number.isSafeInteger(Number(newSaleId)) || Number(newSaleId) <= 0) {
                        const missingIdError = new Error('The server reported success without a valid sale ID. Verify the sale before retrying.');
                        missingIdError.code = 'SALE_ID_MISSING';
                        throw missingIdError;
                    }

                    useToast().displayToast({ message: 'Sale created successfully', variant: 'success' });
                    this.lastCreatedSaleId = newSaleId;

                    const serverAllocations = Array.isArray(res.data?.allocations) ? res.data.allocations : [];

                    if (serverAllocations.length > 0) {
                        await finalizeSaleAllocations(serverAllocations, {
                            originBranchId: isGeneral ? null : branchId,
                            reservedItems: isGeneral ? [] : items,
                        });
                    } else if (!isGeneral) {
                        await offlineDB.transaction('rw', offlineDB.branch_products, async () => {
                            for (const item of items) {
                                const key = `${branchId}-${Number(item.id)}`;
                                const record = await offlineDB.branch_products.get(key);
                                if (!record) continue;
                                record.real_qty = Math.max(0, normalizeQuantity(record.real_qty) - normalizeQuantity(item.qty));
                                record.reserved_qty = Math.max(
                                    0,
                                    normalizeQuantity(record.reserved_qty) - normalizeQuantity(item._lastQty ?? item.qty),
                                );
                                await offlineDB.branch_products.put(record);
                            }
                        });
                    }

                    // Clear the tab that actually submitted, even if another
                    // tab became active while the request was in flight.
                    if (this.tabs[tabId]) {
                        await this.clearTab(tabId);
                    } else if (!isGeneral) {
                        reservationGeneration++;
                        await this._refreshAllTabsMaxQty(branchId);
                    }
                    return newSaleId;
                } else {
                    useToast().displayToast({ message: res.data?.message || 'Sale failed', variant: 'error' });
                    const rejection = new Error(res.data?.message || 'Sale failed');
                    rejection.code = 'SALE_REJECTED';
                    throw rejection;
                }
            } catch (err) {
                if (err?.code === 'ERR_OFFLINE_WRITE') {
                    useToast().displayToast({
                        message: 'You are offline — changes are disabled until the connection returns.',
                        variant: 'warning',
                    });
                    return;
                }

                const status = err?.response?.status;
                const responseCode = err?.response?.data?.code;
                const isStockConflict = status === 409 && (responseCode === 'STOCK_CONFLICT' || Array.isArray(err?.response?.data?.availability));
                const isConcurrencyExhausted = responseCode === 'CONCURRENCY_RETRY_EXHAUSTED';

                if (responseCode === 'CREDIT_LIMIT_EXCEEDED') {
                    // Nothing was committed — the server checks credit before it
                    // touches stock. Keep the cart and payment open, and hand the
                    // server's figures back so the cashier can approve them.
                    this.creditLimitRejection = {
                        tabId,
                        context: err.response?.data?.credit ?? null,
                        message: err.response?.data?.message || 'This sale would exceed the customer credit limit.',
                    };
                    return;
                }

                if (responseCode === 'SALE_DECIMALS_CHANGED') {
                    // Nothing was committed: the precision check sits above the
                    // stock transaction. The setting moved while this cart was
                    // open, so reload it — the cart reprices at the server's
                    // places — and keep the payment open for the cashier to
                    // check the new total before submitting again.
                    useToast().displayToast({
                        message: err.response?.data?.message || 'The decimal places for sales were changed. Check the totals and submit again.',
                        variant: 'warning',
                        duration: 8000,
                    });
                    useConfigurationStore()
                        .fetchConfigurations()
                        .catch(() => null);
                    return;
                }

                if (responseCode === 'TILL_SESSION_REQUIRED' || responseCode === 'TILL_BRANCH_MISMATCH') {
                    // Nothing was committed: the till check sits above the stock
                    // transaction. Keep the cart and the payment open, tell the
                    // cashier, and put the till panel in front of them.
                    useToast().displayToast({
                        message: err.response?.data?.message || 'Open a till before taking cash.',
                        variant: 'warning',
                        duration: 8000,
                    });
                    const tillStore = useTillStore();
                    tillStore.refreshSession().catch(() => null);
                    tillStore.requestPanel();
                    return;
                }

                if (responseCode === 'ACCOUNT_REQUIRED') {
                    // Nothing was committed: the tender check sits above the
                    // stock transaction beside the till gate. No card machine
                    // resolved for this tender at the payment branch — the
                    // one the modal showed has been moved or disabled since —
                    // so the money needs a bank account. Keep the cart and the
                    // payment open and hand the account field to the cashier.
                    // A split names the leg (`tender_index`, errors keyed
                    // tenders.N.account_id); a single tender has none.
                    const tenderIndex = Number.isInteger(err.response?.data?.tender_index) ? err.response.data.tender_index : null;
                    const message =
                        (tenderIndex !== null ? err.response?.data?.errors?.[`tenders.${tenderIndex}.account_id`]?.[0] : null) ||
                        err.response?.data?.errors?.account_id?.[0] ||
                        err.response?.data?.message ||
                        'Select the account this payment goes into.';
                    useToast().displayToast({ message, variant: 'warning', duration: 8000 });
                    this.paymentModal = true;
                    this.accountRequiredRejection = { tabId, message, tenderIndex };
                    return;
                }

                if (isStockConflict || isConcurrencyExhausted) {
                    // Keep cart + payment open and reuse the same client_request_id.
                    const shortfall = Array.isArray(err.response?.data?.shortfall) ? err.response.data.shortfall : [];
                    // Batch mode: a batch held less than its line asked for. The
                    // server's figure becomes the line's cap, so the cashier
                    // lowers it there and picks another batch for the rest.
                    const batchLeft = new Map(
                        shortfall
                            .filter((row) => row?.reason === 'batch_short' && row.receiving_item_id != null)
                            .map((row) => [String(row.receiving_item_id), normalizeQuantity(row.available)]),
                    );
                    if (batchLeft.size && this.tabs[tabId]) {
                        const current = this.tabs[tabId];
                        this._setTab(tabId, {
                            ...current,
                            items: current.items.map((item) =>
                                batchLeft.has(String(item.receiving_item_id ?? ''))
                                    ? {
                                          ...item,
                                          batch_qty: batchLeft.get(String(item.receiving_item_id)),
                                          maxQty: Math.min(
                                              Number.isFinite(Number(item.maxQty)) ? Number(item.maxQty) : Number.MAX_SAFE_INTEGER,
                                              batchLeft.get(String(item.receiving_item_id)),
                                          ),
                                      }
                                    : item,
                            ),
                        });
                    }
                    await this.applyServerAvailability(err.response?.data ?? {});
                    // Batch mode: carry what a batch could not cover onto the
                    // next ones. The cart has then changed under the payment, so
                    // the modal closes and the cashier takes payment again on
                    // the new total.
                    const batchRepaired = await this.repairBatchRefusals(tabId, shortfall, batchPool);
                    if (batchRepaired) {
                        this.paymentModal = false;
                        this.paymentTabId = null;
                        useToast().displayToast({
                            message: `${err.response?.data?.message || 'A batch on this sale could not cover its line.'} The rest was moved onto the next batches — check the total and take payment again.`,
                            variant: 'warning',
                            duration: 10000,
                        });
                        if (!isGeneral && branchId) {
                            reservationGeneration++;
                            await this._refreshAllTabsMaxQty(branchId);
                        }
                        return;
                    }
                    // A batch refusal names the batch and says what to do; the
                    // generic per-variant line would only say "only N available".
                    const isBatchRefusal = shortfall.some((row) => String(row?.reason ?? '').startsWith('batch_'));
                    const detail = shortfall
                        .slice(0, 2)
                        .map((row) => `#${row.product_variant_id}: only ${row.available ?? 0} available`)
                        .join('; ');
                    useToast().displayToast({
                        message: isConcurrencyExhausted
                            ? 'The system was busy. Nothing was charged — please try again.'
                            : isBatchRefusal
                              ? err.response?.data?.message || 'A batch on this sale cannot cover its line. Nothing was charged.'
                              : detail
                                ? `Stock changed while completing this sale. ${detail}. Nothing was charged.`
                                : err.response?.data?.message || 'Stock changed while completing this sale. Nothing was charged.',
                        variant: 'warning',
                        duration: 10000,
                    });
                    if (!isGeneral && branchId) {
                        reservationGeneration++;
                        await this._refreshAllTabsMaxQty(branchId);
                    }
                    return;
                }

                const isAmbiguous =
                    err?.code === 'ECONNABORTED' ||
                    err?.code === 'ERR_NETWORK' ||
                    status === 408 ||
                    status === 429 ||
                    (status >= 500 && status < 600);

                if (isAmbiguous) {
                    useToast().displayToast({
                        message: 'Sale outcome unknown — verify before retrying. Stock was not released.',
                        variant: 'warning',
                        duration: 10000,
                    });
                    // Keep reservations; force a stock resync when possible
                    try {
                        await syncBranchStock(branchId, true);
                        reservationGeneration++;
                        await this._refreshAllTabsMaxQty(branchId);
                    } catch (syncErr) {
                        console.warn('post-timeout stock sync failed', syncErr);
                    }
                } else {
                    useToast().displayToast({
                        message: err.response?.data?.message || err.message || 'Network / Server error',
                        variant: 'error',
                    });
                    // Definite client rejection (typical 4xx except 408/429): roll back reservations
                    const isDefiniteRejection = err?.code === 'SALE_REJECTED' || (status && status >= 400 && status < 500);
                    if (branchId && items?.length && isDefiniteRejection && this.tabs[tabId]) {
                        // The rejected sale is still in the cart, so its local
                        // reservations must remain represented.
                        reservationGeneration++;
                        await this._refreshAllTabsMaxQty(branchId);
                    }
                }
            } finally {
                if (this.tabs[tabId]) {
                    this._setTab(tabId, {
                        ...this.tabs[tabId],
                        isSubmitting: false,
                    });
                }
            }
        },
    },
});